import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { RefreshCw, ArrowUpRight, Zap, ShieldCheck, ShieldAlert, Star, Layers, ChevronDown, ChevronRight, User, Rocket, Copy, Info, Flame, TrendingUp, Check, ExternalLink, Clock, Gem, Target, Eye, Ban, Sparkles, Search, X, Repeat, Skull } from "lucide-react";
import { MiniCandleChart } from "@/components/MiniCandleChart";

import * as nursery from "@/lib/nurseryEngine";
import type { NurserySnapshot } from "@/lib/nurseryEngine";
import { useApp } from "@/context/AppContext";
import { tokenMetaCache } from "@/lib/tokenMetaCache";
import { normalizeCoinImageUri } from "@/lib/imageUtils";
import { fetchPumpFunProfile, type PumpFunProfile } from "@/lib/pumpfunProfile";
import { deployerStore } from "@/lib/deployerStore";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { DeployerRulesModal } from "./DeployerRulesModal";
import { prefetchCandles, prefetchBatchCandles } from "@/lib/pumpCandles";


import { getPumpPortalTradingWalletPubkey } from "@/lib/pumpPortalConfig";

function AnimatedNumber({ value }: { value: number }) {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    let startTimestamp: number | null = null;
    const duration = 1500; // 1.5 seconds
    const startValue = displayValue;
    const difference = value - startValue;
    
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      // easeOutExpo
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setDisplayValue(startValue + difference * easeProgress);
      
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    
    window.requestAnimationFrame(step);
  }, [value]);
  
  return <>{displayValue.toLocaleString("en-US", { maximumFractionDigits: 0 })}</>;
}

export function computeUniversalHeat(c: any) {
  if (c.qualityScore) return c.qualityScore;
  let score = c.runnerScore || 0;
  const mc = c.marketCapSol || (c.marketCapUsd ? c.marketCapUsd / 150 : 0);
  const peakMc = c.peakMcSol || 0;
  if (mc > 0) {
    score += Math.min(Math.log10(mc) * 5, 25);
    if (peakMc > 0) {
      const retention = mc / peakMc;
      if (retention > 0.8) score += 15;
      else if (retention > 0.5) score += 10;
      else if (retention > 0.3) score += 5;
    }
  }
  if (c.isDead || c.isRugged) score = Math.max(0, score - 50);
  return Math.round(score);
}

function formatDuration(ms: number) {
  if (!ms || ms < 0) return "0s";
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const totalMin = Math.floor(totalSec / 60);
  if (totalMin < 60) return `${totalMin}m`;
  const totalHr = Math.floor(totalMin / 60);
  if (totalHr < 24) {
    const m = totalMin % 60;
    return m > 0 ? `${totalHr}h ${m}m` : `${totalHr}h`;
  }
  const totalDays = Math.floor(totalHr / 24);
  if (totalDays < 7) {
    const h = totalHr % 24;
    return h > 0 ? `${totalDays}d ${h}h` : `${totalDays}d`;
  }
  const totalWeeks = Math.floor(totalDays / 7);
  if (totalWeeks < 4) {
    const d = totalDays % 7;
    return d > 0 ? `${totalWeeks}w ${d}d` : `${totalWeeks}w`;
  }
  const totalMonths = Math.floor(totalDays / 30);
  if (totalMonths < 12) return `${totalMonths}mo`;
  const years = Math.floor(totalMonths / 12);
  const mo = totalMonths % 12;
  return mo > 0 ? `${years}y ${mo}mo` : `${years}y`;
}

// ── Tab component ─────────────────────────────────────────────────────────────

type Tab = "my" | "new" | "grad" | "bonded" | "old" | "runners" | "devs";

function TabButton({ active, children, onClick, count, flashDelay = 0 }: { active: boolean; children: React.ReactNode; onClick: () => void; count?: number; flashDelay?: number }) {
  const [changed, setChanged] = useState(false);
  const firstRender = useRef(true);
  const prevCount = useRef<number | undefined>(undefined);
  
  useEffect(() => {
    if (count === undefined) return;
    if (firstRender.current) {
      firstRender.current = false;
      prevCount.current = count;
      return;
    }
    if (count !== prevCount.current) {
      prevCount.current = count;
      const delayId = setTimeout(() => {
        setChanged(true);
        const id = setTimeout(() => setChanged(false), 400);
        return () => clearTimeout(id);
      }, flashDelay);
      return () => clearTimeout(delayId);
    }
  }, [count, flashDelay]);

  return (
    <button
      onClick={onClick}
      title={count !== undefined ? `${count.toLocaleString()} tokens` : undefined}
      className={`flex-1 min-w-0 flex flex-col items-center justify-center py-2 sm:py-2.5 px-0.5 border-b-2 transition-all group overflow-hidden ${
        active 
          ? "border-emerald-500 bg-emerald-500/10" 
          : "border-transparent hover:bg-white/5"
      }`}
    >
      {count !== undefined && (
        <span 
          className={`text-[11px] sm:text-xs md:text-[13px] font-mono font-black tabular-nums tracking-tighter leading-none transition-all duration-300 truncate max-w-full block ${
            changed 
              ? "scale-105 text-emerald-300 drop-shadow-[0_0_8px_rgba(52,211,153,0.8)]" 
              : active 
                ? "text-emerald-400 drop-shadow-[0_0_4px_rgba(52,211,153,0.4)]" 
                : "text-[var(--color-fg-dim)] group-hover:text-white"
          }`}
        >
          {count.toLocaleString()}
        </span>
      )}
      <span className={`text-[8px] sm:text-[9px] font-black uppercase tracking-wider sm:tracking-widest mt-1 transition-colors truncate max-w-full block ${
        active ? "text-emerald-500/80" : "text-[var(--color-fg-dim)]/50 group-hover:text-[var(--color-fg-dim)]"
      }`}>
        {children}
      </span>
    </button>
  );
}


function DeployerAvatar({ address, size = "size-8" }: { address: string, size?: string }) {
  const [profile, setProfile] = useState<PumpFunProfile | null>(null);
  
  useEffect(() => {
    if (!address) return;
    let isMounted = true;
    fetchPumpFunProfile(address).then(p => {
      if (isMounted) setProfile(p);
    });
    return () => { isMounted = false; };
  }, [address]);

  if (!profile) return null;

  return (
    <div className={`${size} rounded-full overflow-hidden border border-white/20 bg-black/50 shadow-lg shrink-0 relative group/avatar z-20`} title={`Deployer: ${profile.username}`}>
      <img src={profile.avatar} alt="Deployer" className="w-full h-full object-cover bg-black/40" loading="lazy" />
      <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-full" />
    </div>
  );
}

// ── Pre-bond row ──────────────────────────────────────────────────────────────

export function PrebondRow({ token, showProgress: _showProgress, isSelected, onOpen }: {
  token: any; showProgress: boolean; isSelected: boolean; onOpen: (mint: string) => void;
}) {
  const isMobile = useIsMobile();
  const cached = tokenMetaCache.get(token.mint);
  const displayImage = normalizeCoinImageUri(cached?.imageUri || token.imageUri, token.mint);
  const displayName = cached?.name || token.name || `Token ${token.mint.slice(0,4)}`;
  const displaySymbol = cached?.symbol || token.symbol || token.mint.slice(0,4).toUpperCase();

  const GRAD_SOL = 79;
  const pct = Math.min(100, Math.max(0, Math.round((token.marketCapSol / GRAD_SOL) * 100)));
  const total = token.buys5m + token.sells5m;
  const buyPct = total > 0 ? Math.round((token.buys5m / total) * 100) : 50;

  // Vary image size based on market cap — capped at size-20 so it never covers progress bars
  const imageSize = token.marketCapSol > 50 ? 'size-20' : token.marketCapSol > 25 ? 'size-16' : token.marketCapSol > 8 ? 'size-14' : 'size-12';
  const mobileImageSize = isMobile ? 'size-16' : imageSize;

  // Random visual effects for engagement
  const randomEffect = useMemo(() => {
    const effects = [
      'hover:scale-110 hover:rotate-3',
      'hover:scale-110 hover:-rotate-3',
      'hover:scale-110 hover:rotate-6',
      'hover:scale-110 hover:-rotate-6',
      'hover:scale-110',
      'hover:scale-110 hover:rotate-12',
    ];
    const hash: number = Array.from(token.mint as string).reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
    return effects[hash % effects.length];
  }, [token.mint]);

  const randomGlow = useMemo(() => {
    const glows = [
      'hover:shadow-[0_0_30px_rgba(16,185,129,0.6)]',
      'hover:shadow-[0_0_30px_rgba(168,85,247,0.6)]',
      'hover:shadow-[0_0_30px_rgba(245,158,11,0.6)]',
      'hover:shadow-[0_0_30px_rgba(59,130,246,0.6)]',
      'hover:shadow-[0_0_30px_rgba(236,72,153,0.6)]',
    ];
    const hash: number = Array.from(token.mint as string).reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
    return glows[hash % glows.length];
  }, [token.mint]);

  const randomPulse = useMemo(() => {
    const pulses = ['', 'animate-pulse-slow', 'animate-pulse'];
    const hash: number = Array.from(token.mint as string).reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
    return pulses[hash % pulses.length];
  }, [token.mint]);

  const imageAnimation = `transition-all duration-700 ease-out ${randomEffect} ${randomGlow} ${randomPulse}`;

  // Map image size to left positioning for details and progress bar width to keep them in sync
  const detailsLeftOffset = {
    'size-20': 'left-28',
    'size-16': 'left-24',
    'size-14': 'left-22',
    'size-12': 'left-20',
  }[imageSize] || 'left-24';


  // Advanced Signals
  const drawdownPct = token.peakMcSol > 0 ? Math.round(((token.peakMcSol - token.marketCapSol) / token.peakMcSol) * 100) : 0;
  const isDipBuy = drawdownPct > 20 && token.vol5mSol >= 10 && token.momentumBurst;

  let botPnl = 0;
  let botStatus = "";
  if (token.phantomTrade) {
    if (token.phantomTrade.status === "open") {
      botPnl = ((token.marketCapSol - token.phantomTrade.entryMcSol) / token.phantomTrade.entryMcSol) * 100;
      botStatus = "open";
    } else {
      botPnl = token.phantomTrade.pnlPct || 0;
      botStatus = token.phantomTrade.status;
    }
  }

  const ageMs = Date.now() - token.firstSeenMs;
  const ageMinutes = Math.max(0.1, ageMs / 60000);
  const solPerMinute = token.marketCapSol / ageMinutes;
  let etaBadge = null;
  if (solPerMinute > 0 && token.marketCapSol > 15 && token.marketCapSol < GRAD_SOL) {
    const solRemaining = GRAD_SOL - token.marketCapSol;
    const minsRemaining = solRemaining / solPerMinute;
    if (minsRemaining < 60) {
      etaBadge = Math.max(1, Math.round(minsRemaining));
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onMouseEnter={() => prefetchCandles(token.mint, "1s")}
      onTouchStart={() => prefetchCandles(token.mint, "1s")}
      onClick={() => { prefetchCandles(token.mint, "1s"); onOpen(token.mint); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpen(token.mint); }}
      className={`group w-full border-b border-[var(--color-border-subtle)] ${isMobile ? 'px-3 py-2' : 'px-4 py-3'} text-left transition-all relative cursor-pointer outline-none hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] hover:border-emerald-500/20 ${
        isSelected ? "bg-emerald-500/[0.04]" : "hover:bg-white/[0.02]"
      }`}
    >
      {isSelected && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500 shadow-[2px_0_10px_rgba(16,185,129,0.3)]" />
      )}

      {/* Chart takes full space as background */}
      <div className={`relative h-[280px]`}>
        <div className="absolute inset-0 bg-black/60 rounded-xl overflow-hidden border border-white/5 z-0">
          <MiniCandleChart
            mint={token.mint}
            height={280}
            rightOffset={isMobile ? 10 : 30}
            showLogo={false}
          />
        </div>

        {/* Top left: name, description, CA, volume, whale count, progress bar */}
        <div className={`absolute ${isMobile ? 'left-4 top-4 right-12' : 'left-4 top-4 max-w-[280px]'} z-10 flex flex-col gap-1`}>
          <div className="flex items-center gap-2 leading-none">
            <span className={`font-black bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent animate-gradient-x ${isMobile ? 'text-[13px]' : 'text-[16px]'}`}>{displaySymbol}</span>
            {token.creator && (
              <div className={`flex items-center gap-0.5 px-1 rounded-[2px] bg-black/40 border border-white/5 ${deployerStore.getTag(token.creator).color}`}>
                {deployerStore.getTag(token.creator).isSerial ? <ShieldAlert className="size-1.5" /> : <ShieldCheck className="size-1.5" />}
                <span className="text-[6px] font-black uppercase tracking-tighter">{deployerStore.getTag(token.creator).label}</span>
              </div>
            )}
          </div>
          <span className={`font-bold bg-gradient-to-r from-white/80 to-white/50 bg-clip-text text-transparent truncate ${isMobile ? 'text-[10px]' : 'text-[11px]'}`}>
            {displayName} {token.isPresaved && <span className="ml-1 text-[9px] font-black uppercase text-amber-400 bg-amber-500/10 px-1 rounded">Draft</span>}
          </span>
          {cached?.description && (
            <span className={`font-sans text-white/50 leading-tight line-clamp-2 mt-0.5 ${isMobile ? 'text-[9px]' : 'text-[10px]'}`} title={cached.description}>
              {cached.description}
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(token.mint); }}
            className={`font-mono text-[8.5px] text-white/30 hover:text-white/70 truncate text-left transition-colors cursor-pointer active:scale-95 flex items-center gap-1 w-full max-w-[120px]`}
            title="Click to copy CA"
          >
            {token.mint.slice(0, 6)}...{token.mint.slice(-6)}
            <Copy className="size-2 opacity-50 shrink-0" />
          </button>
          {token.creator && !isMobile && (
            <button
              onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(token.creator); }}
              className="font-mono text-[8px] text-blue-400/70 hover:text-blue-400 truncate leading-none transition-colors cursor-pointer"
              title="Click to copy creator address"
            >
              CA: {token.creator.slice(0, 8)}...{token.creator.slice(-6)}
            </button>
          )}
          {/* Narrative Social Links */}
          {!isMobile && (cached?.twitter || cached?.telegram || cached?.website) && (
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {cached?.twitter && (
                <a href={cached.twitter.startsWith('http') ? cached.twitter : `https://x.com/${cached.twitter.replace('@','')}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-[8px] font-bold text-sky-400 hover:bg-sky-500/20 transition-all">
                  𝕏 Twitter
                </a>
              )}
              {cached?.telegram && (
                <a href={cached.telegram.startsWith('http') ? cached.telegram : `https://t.me/${cached.telegram}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-[8px] font-bold text-blue-400 hover:bg-blue-500/20 transition-all">
                  ✈ TG
                </a>
              )}
              {cached?.website && (
                <a href={cached.website} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-[8px] font-bold text-purple-400 hover:bg-purple-500/20 transition-all">
                  🌐 Site
                </a>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-1 max-w-[280px]">
            {/* Time Pills */}
            <span className="flex items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-0.5 text-[8.5px] font-mono text-indigo-400 border border-indigo-500/20" title="Time since creation">
              🕒 {formatDuration(Date.now() - (token.createdAt || token.firstSeenMs || Date.now()))}
            </span>
            <span className="flex items-center gap-1 rounded-full bg-teal-500/10 px-1.5 py-0.5 text-[8.5px] font-mono text-teal-400 border border-teal-500/20" title="Time running (since bonded or created)">
              🏃 {formatDuration(Date.now() - (token.bondedMs || token.createdAt || token.firstSeenMs || Date.now()))}
            </span>
            {/* Dynamic Volume Pill */}
            <span 
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[8.5px] font-mono font-bold transition-all ${
                token.vol5mSol >= 50 ? "bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/50 animate-pulse shadow-[0_0_10px_rgba(217,70,239,0.5)]" :
                token.consecutiveBuys >= 5 ? "bg-amber-500/20 text-amber-400 border-amber-500/50 animate-pulse shadow-[0_0_10px_rgba(245,158,11,0.5)]" :
                (token.momentumBurst && token.vol5mSol >= 10) ? "bg-orange-500/20 text-orange-400 border-orange-500/40 shadow-[0_0_8px_rgba(249,115,22,0.4)] animate-[pulse_2s_ease-in-out_infinite]" :
                token.vol5mSol >= 5 ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" :
                "bg-white/5 text-[var(--color-fg-dim)] border-white/10"
              }`}
              title="5m Volume"
            >
              {(token.vol5mSol >= 10 || token.consecutiveBuys >= 5 || token.momentumBurst) && <Zap className="size-2.5" fill="currentColor" />}
              {fmtSol(token.vol5mSol)} VOL
            </span>
            {/* Unaudited Custom Logic Badge */}
            {(token as any).hasCustomHooks && (
              <span className="flex items-center gap-1 rounded-full bg-rose-500/10 px-1.5 py-0.5 text-[8.5px] font-bold text-rose-500 border border-rose-500/30 shadow-[0_0_8px_rgba(244,63,94,0.4)] animate-pulse" title="This token uses custom, unaudited smart contract logic. Risk of honeypots or malicious code.">
                <ShieldAlert className="size-2.5" />
                UNAUDITED
              </span>
            )}
            
            {/* Holder count proxy pill */}
            <span className="flex items-center gap-1 rounded-full bg-blue-500/10 px-1.5 py-0.5 text-[8.5px] font-mono text-blue-400 border border-blue-500/20" title="Holders / Active Traders">
              <User className="size-2.5" />
              {token.holderCount || token.traderCount || 0}
            </span>

            {/* Whale Max Buy */}
            {!isMobile && token.maxSingleBuySol > 0.1 && (
              <span className="flex items-center gap-0.5 rounded-full bg-white/5 px-1.5 py-0.5 text-[8.5px] font-mono text-white/60 border border-white/10" title="Max Single Buy">
                <span>🐋</span>{fmtSol(token.maxSingleBuySol)}
              </span>
            )}

            {/* Whale Cluster */}
            {!isMobile && token.largeTradeCluster > 1 && (
              <span className="flex items-center gap-0.5 rounded-full bg-sky-500/20 px-1.5 py-0.5 text-[8.5px] font-mono text-sky-400 border border-sky-500/30 shadow-[0_0_8px_rgba(14,165,233,0.3)] animate-[pulse_2s_ease-in-out_infinite]" title="Whale Cluster (Multiple 1+ SOL Buys)">
                <span>🦈</span>x{token.largeTradeCluster}
              </span>
            )}

            {/* Runner Score (Heat) */}
            {computeUniversalHeat(token) > 0 && (
              <span 
                className={`flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8.5px] font-mono font-bold border ${
                  computeUniversalHeat(token) >= 75 
                    ? "bg-rose-500/20 text-rose-400 border-rose-500/40 shadow-[0_0_8px_rgba(244,63,94,0.4)] animate-[pulse_2s_ease-in-out_infinite]"
                    : computeUniversalHeat(token) >= 50
                    ? "bg-orange-500/20 text-orange-400 border-orange-500/40"
                    : "bg-white/5 text-[var(--color-fg-dim)] border-white/10"
                }`} 
                title="Runner Score (Heat)"
              >
                🔥 {computeUniversalHeat(token).toLocaleString()}
              </span>
            )}

            {/* Peak Multiplier (Runners) */}
            {(token as any).multiplier !== undefined && (
              <>
                <span 
                  className={`flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8.5px] font-mono font-bold border bg-amber-500/20 text-amber-400 border-amber-500/40`} 
                  title="Peak Multiplier"
                >
                  🚀 {Number((token as any).multiplier).toLocaleString("en-US", {minimumFractionDigits: 1, maximumFractionDigits: 1})}x Peak
                </span>
                {(token as any).peakMcUsd > 0 && (
                  <span 
                    className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[9px] font-mono font-black border bg-emerald-500/20 text-emerald-400 border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.5)] animate-[pulse_2s_ease-in-out_infinite]`}
                    title="Market Cap (Current / ATH)"
                  >
                    <span>{fmtUsd(token.marketCapUsd)}</span>
                    <span className="text-emerald-500/50">|</span>
                    <span className="text-amber-400 drop-shadow-[0_0_5px_rgba(251,191,36,0.8)]">ATH {fmtUsd((token as any).peakMcUsd)}</span>
                  </span>
                )}
              </>
            )}

            {/* Dip Buy / Drawdown */}
            {!isMobile && drawdownPct > 15 && (
              <span className={`flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8.5px] font-mono border ${
                isDipBuy ? "bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/40 shadow-[0_0_8px_rgba(217,70,239,0.3)] animate-pulse" : "bg-rose-500/10 text-rose-400 border-rose-500/20"
              }`} title={isDipBuy ? "Strong Dip Buy Signal" : `Down ${drawdownPct}% from Peak`}>
                {isDipBuy ? "🔥 DIP BUY" : `▼${drawdownPct}%`}
              </span>
            )}

            {/* Bot PnL */}
            {!isMobile && token.phantomTrade && (
              <span className={`flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8.5px] font-mono border ${
                botStatus === 'won' ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 shadow-[0_0_8px_rgba(16,185,129,0.3)]" :
                botStatus === 'lost' ? "bg-rose-500/10 text-rose-500/70 border-rose-500/20" :
                botPnl >= 0 ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20"
              }`} title="Bot Paper Trade PnL">
                🤖 {botPnl > 0 ? "+" : ""}{botPnl.toLocaleString("en-US", {minimumFractionDigits: 1, maximumFractionDigits: 1})}%
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <div className={`${isMobile ? 'w-1/4' : 'flex-1'} rounded-full bg-white/[0.05] overflow-hidden relative h-1`}>
              <div
                className={`h-full transition-all duration-700 ease-out bg-gradient-to-r from-emerald-500 via-green-400 to-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.5)]`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className={`font-mono font-bold ${buyPct >= 50 ? "text-emerald-400" : "text-rose-400"} text-[9px]`}>{buyPct}%</span>

            {/* ETA Badge */}
            {!isMobile && etaBadge && (
              <span className="font-mono text-[8.5px] text-emerald-400/80 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 flex items-center gap-0.5" title="Estimated Time to Graduation">
                ⏱️ {etaBadge}m
              </span>
            )}
          </div>

          {/* Lily Quality Gate Stats Strip */}
          {nursery.isServerSideActive() && (
            <div className="flex items-center gap-2 mt-1.5 text-[9.5px] font-semibold border-t border-white/5 pt-1 w-full flex-wrap">
              <span className={devTone(token.creatorPct)}>
                <span className="opacity-50">Dev</span> {pctStr(token.creatorPct)}
              </span>
              <span className={t10Tone(token.holderTop10)}>
                <span className="opacity-50">T10</span> {pctStr(token.holderTop10)}
              </span>
              <span className={dipTone(token.dipPct)}>
                <span className="opacity-50">Dip</span> {token.dipPct != null ? `${token.dipPct.toLocaleString("en-US", {maximumFractionDigits: 0})}%` : '—'}
              </span>
              <span key={token.hidden ? 'warning' : !token.checked ? 'checking' : token.revived ? 'revival' : (token.marketCapUsd ?? 0) >= 3000 ? 'tradable' : 'clean'} className={`ml-auto px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border animate-in fade-in zoom-in duration-500 shadow-lg ${
                token.hidden
                  ? "bg-rose-500/10 text-rose-400 border-rose-500/50 shadow-rose-500/20"
                  : !token.checked
                  ? "bg-amber-500/10 text-amber-400 border-amber-500/50 shadow-amber-500/20 animate-pulse"
                  : token.revived
                  ? "bg-sky-500/10 text-sky-400 border-sky-500/50 shadow-sky-500/20"
                  : (token.marketCapUsd ?? 0) >= 3000
                  ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/50 shadow-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.5)]"
                  : "bg-white/5 text-[var(--color-fg-dim)] border-white/20"
              }`} title={token.hideReason || undefined}>
                {token.hidden
                  ? `warning: ${token.hideReason || "gate"}`
                  : !token.checked
                  ? "checking…"
                  : token.revived
                  ? "revival"
                  : (token.marketCapUsd ?? 0) >= 3000
                  ? "tradable"
                  : "clean"}
              </span>
            </div>
          )}
        </div>

        {/* Top right: star button */}
        <div className={`absolute ${isMobile ? 'right-2 top-2' : 'right-4 top-4'} z-10`}>
          <button
            onClick={(e) => { e.stopPropagation(); }}
            className="p-2 rounded-full bg-black/40 border border-white/10 hover:bg-amber-500/20 hover:border-amber-500/30 transition-all hover:scale-110 hover:shadow-[0_0_20px_rgba(245,158,11,0.4)]"
          >
            <Star className="size-4 text-amber-400/50 hover:text-amber-400 transition-colors" />
          </button>
        </div>

        {/* Bottom left: image */}
        <div className={`absolute ${isMobile ? 'left-4 bottom-4' : 'left-4 bottom-4'} z-10 flex items-end gap-1.5`}>
          <div className={`shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-black/40 shadow-[0_0_40px_rgba(0,0,0,0.8),0_0_20px_rgba(16,185,129,0.3)] ${mobileImageSize} ${imageAnimation}`}>
            {displayImage ? (
              <img src={displayImage} alt={displaySymbol} className="h-full w-full object-cover" loading="lazy" decoding="async" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[var(--color-fg-dim)]/20">
                <Zap className={isMobile ? 'size-4' : 'size-6'} />
              </div>
            )}
          </div>
          {token.creator && (
            <DeployerAvatar address={token.creator} size={isMobile ? "size-6" : "size-8"} />
          )}
        </div>

        {/* Bottom right: action buttons and other info */}
        <div className={`absolute ${isMobile ? 'left-20 right-4 bottom-4' : `${detailsLeftOffset} right-4 bottom-4`} z-10 flex flex-col gap-2 items-end`}>
          <div className="flex flex-col items-end gap-0.5 bg-black/60 px-2 py-1.5 rounded-xl border border-white/5 backdrop-blur-md">
            <div className="flex items-center gap-1.5">
              <span className="text-[8px] font-bold text-white/40 uppercase tracking-widest">MC</span>
              <span className={`font-mono font-black text-emerald-400 ${isMobile ? 'text-[11px]' : 'text-[12px]'}`}>{fmtSol(token.marketCapSol)}</span>
            </div>
            {token.peakMcSol > token.marketCapSol * 1.05 && (
              <div className="flex items-center gap-1.5">
                <span className="text-[8px] font-bold text-white/40 uppercase tracking-widest">ATH</span>
                <span className="font-mono font-black text-amber-400/80 text-[10px]">{fmtSol(token.peakMcSol)}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <span className="text-[8px] font-bold text-white/40 uppercase tracking-widest">24H VOL</span>
              <span className="font-mono font-black text-cyan-400 text-[10px] animate-pulse">{fmtSol(token.vol24h || token.vol5mSol || token.volumeUsd || 0)}</span>
            </div>
            {token.lastTradeMs > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-[8px] font-bold text-white/40">Last tx</span>
                <span className="font-mono text-[8px] text-white/40">{fmtAge(token.lastTradeMs)}</span>
              </div>
            )}
            <span className="font-mono text-white/30 text-[8px]">{fmtAge(token.firstSeenMs)} ago</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); }}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 transition-all"
            >
              <ArrowUpRight className="size-3" />
              <span className="text-[10px] font-bold">{token.buys5m || 0}</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); }}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-rose-500/10 text-rose-400 transition-all"
            >
              <ArrowUpRight className="size-3 rotate-180" />
              <span className="text-[10px] font-bold">{token.sells5m || 0}</span>
            </button>

            {/* Mobile Details Section - Next to up/down arrows */}
            {isMobile && (
              <div className="flex items-center gap-1.5 ml-2">
                <div className="flex flex-col">
                  <span className="text-[6px] font-bold text-white/40 uppercase tracking-wider">MC</span>
                  <span className="text-[8px] font-mono font-black text-emerald-400">{fmtSol(token.marketCapSol)}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[6px] font-bold text-white/40 uppercase tracking-wider">LQ</span>
                  <span className="text-[8px] font-mono font-black text-blue-400">{fmtSol(token.liquiditySol || 0)}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[6px] font-bold text-white/40 uppercase tracking-wider">24H</span>
                  <span className="text-[8px] font-mono font-black text-amber-400">{fmtSol(token.vol24hSol || 0)}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[6px] font-bold text-white/40 uppercase tracking-wider">1H</span>
                  <span className="text-[8px] font-mono font-black text-purple-400">{fmtSol(token.vol1hSol || 0)}</span>
                </div>
              </div>
            )}
            {!isMobile && (
              <div className="flex items-center">
                {[10, 25, 50, 100].map((pct, index) => (
                  <button
                    key={pct}
                    onClick={(e) => { e.stopPropagation(); }}
                    className={`px-1.5 py-1 text-[9px] font-bold transition-all border border-emerald-500/20 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 ${
                      index === 0 ? 'rounded-l-lg' : index === 3 ? 'rounded-r-lg' : 'border-l-0'
                    }`}
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Bonded row ────────────────────────────────────────────────────────────────

export function BondedRow({ token, showScore, isSelected, onOpen }: {
  token: any; showScore: boolean; isSelected: boolean; onOpen: (mint: string) => void;
}) {
  const isMobile = useIsMobile();
  const cached = tokenMetaCache.get(token.mint);
  const displayImage = normalizeCoinImageUri(cached?.imageUri || token.imageUri, token.mint);
  const displayName = cached?.name || token.name || `Token ${token.mint.slice(0,4)}`;
  const displaySymbol = cached?.symbol || token.symbol || token.mint.slice(0,4).toUpperCase();

  const buyPct = (token.buys1h + token.sells1h) > 0 ? Math.round((token.buys1h / (token.buys1h + token.sells1h)) * 100) : 50;

  // Vary image size — capped at size-20 so it never covers progress bars
  const imageSize = token.marketCapUsd > 100000 ? 'size-20' : token.marketCapUsd > 50000 ? 'size-16' : token.marketCapUsd > 10000 ? 'size-14' : 'size-12';
  const mobileImageSize = isMobile ? 'size-16' : imageSize;

  // Random visual effects for engagement
  const randomEffect = useMemo(() => {
    const effects = [
      'hover:scale-110 hover:rotate-3',
      'hover:scale-110 hover:-rotate-3',
      'hover:scale-110 hover:rotate-6',
      'hover:scale-110 hover:-rotate-6',
      'hover:scale-110',
      'hover:scale-110 hover:rotate-12',
    ];
    const hash: number = Array.from(token.mint as string).reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
    return effects[hash % effects.length];
  }, [token.mint]);

  const randomGlow = useMemo(() => {
    const glows = [
      'hover:shadow-[0_0_30px_rgba(16,185,129,0.6)]',
      'hover:shadow-[0_0_30px_rgba(168,85,247,0.6)]',
      'hover:shadow-[0_0_30px_rgba(245,158,11,0.6)]',
      'hover:shadow-[0_0_30px_rgba(59,130,246,0.6)]',
      'hover:shadow-[0_0_30px_rgba(236,72,153,0.6)]',
    ];
    const hash: number = Array.from(token.mint as string).reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
    return glows[hash % glows.length];
  }, [token.mint]);

  const randomPulse = useMemo(() => {
    const pulses = ['', 'animate-pulse-slow', 'animate-pulse'];
    const hash: number = Array.from(token.mint as string).reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
    return pulses[hash % pulses.length];
  }, [token.mint]);

  const imageAnimation = `transition-all duration-700 ease-out ${randomEffect} ${randomGlow} ${randomPulse}`;

  // Map image size to left positioning for details and progress bar width to keep them in sync
  const detailsLeftOffset = {
    'size-20': 'left-28',
    'size-16': 'left-24',
    'size-14': 'left-22',
    'size-12': 'left-20',
  }[imageSize] || 'left-24';



  return (
    <div
      role="button"
      tabIndex={0}
      onMouseEnter={() => prefetchCandles(token.mint, "1s")}
      onTouchStart={() => prefetchCandles(token.mint, "1s")}
      onClick={() => { prefetchCandles(token.mint, "1s"); onOpen(token.mint); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpen(token.mint); }}
      className={`group w-full border-b border-[var(--color-border-subtle)] ${isMobile ? 'px-3 py-2' : 'px-4 py-3'} text-left transition-all relative cursor-pointer outline-none hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] hover:border-emerald-500/20 ${
        isSelected ? "bg-emerald-500/[0.04]" : "hover:bg-white/[0.02]"
      }`}
    >
      {isSelected && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500 shadow-[2px_0_10px_rgba(16,185,129,0.3)]" />
      )}

      {/* Chart takes full space as background */}
      <div className={`relative h-[280px]`}>
        <div className="absolute inset-0 bg-black/60 rounded-xl overflow-hidden border border-white/5 z-0">
          <MiniCandleChart
            mint={token.mint}
            height={280}
            rightOffset={isMobile ? 10 : 30}
            showLogo={false}
          />
        </div>

        {/* Top left: name, description, CA, volume, whale count, progress bar */}
        <div className={`absolute ${isMobile ? 'left-4 top-4 right-12' : 'left-4 top-4 max-w-[300px]'} z-10 flex flex-col gap-1`}>
          <div className="flex items-center gap-2 leading-none">
            <span className={`font-black bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent animate-gradient-x ${isMobile ? 'text-[13px]' : 'text-[16px]'}`}>{displaySymbol}</span>
            {token.creator && (
              <div className={`flex items-center gap-0.5 px-1 rounded-[2px] bg-black/40 border border-white/5 ${deployerStore.getTag(token.creator).color}`}>
                {deployerStore.getTag(token.creator).isSerial ? <ShieldAlert className="size-1.5" /> : <ShieldCheck className="size-1.5" />}
                <span className="text-[6px] font-black uppercase tracking-tighter">{deployerStore.getTag(token.creator).label}</span>
              </div>
            )}
            {showScore && token.revivalScore > 0 && (
              <span className="text-emerald-400 font-bold text-[10px]">{scoreBar(token.revivalScore)}</span>
            )}
            {Number(token.multiplier) > 0 && (
              <>
                <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-[3px] bg-amber-500/20 border border-amber-500/40 text-[10px] font-black text-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.2)]">
                  🔥 {Number(token.multiplier).toLocaleString("en-US", {minimumFractionDigits: 1, maximumFractionDigits: 1})}x
                </div>
                {token.marketCapUsd > 0 && (
                  <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-[3px] bg-emerald-500/20 border border-emerald-500/40 text-[10px] font-black text-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.2)]">
                    Peak MC: {fmtUsd(token.marketCapUsd)}
                  </div>
                )}
              </>
            )}
          </div>
          <span className={`font-bold bg-gradient-to-r from-white/80 to-white/50 bg-clip-text text-transparent truncate ${isMobile ? 'text-[10px]' : 'text-[11px]'}`}>{displayName}</span>
          {cached?.description && (
            <span className={`font-sans text-white/50 leading-tight line-clamp-2 mt-0.5 max-w-[90%] ${isMobile ? 'text-[9px]' : 'text-[10px]'}`} title={cached.description}>
              {cached.description}
            </span>
          )}
          {!isMobile && (
            <span className="font-mono text-[8.5px] text-white/30 truncate select-all leading-none mt-1" title={token.mint}>
              {token.mint.slice(0, 6)}...{token.mint.slice(-6)}
            </span>
          )}
          {token.creator && !isMobile && (
            <button
              onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(token.creator); }}
              className="font-mono text-[8px] text-blue-400/70 hover:text-blue-400 truncate leading-none transition-colors cursor-pointer"
              title="Click to copy creator address"
            >
              CA: {token.creator.slice(0, 8)}...{token.creator.slice(-6)}
            </button>
          )}
          {/* Narrative Social Links */}
          {!isMobile && (cached?.twitter || cached?.telegram || cached?.website) && (
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {cached?.twitter && (
                <a href={cached.twitter.startsWith('http') ? cached.twitter : `https://x.com/${cached.twitter.replace('@','')}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-[8px] font-bold text-sky-400 hover:bg-sky-500/20 transition-all">
                  𝕏 Twitter
                </a>
              )}
              {cached?.telegram && (
                <a href={cached.telegram.startsWith('http') ? cached.telegram : `https://t.me/${cached.telegram}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-[8px] font-bold text-blue-400 hover:bg-blue-500/20 transition-all">
                  ✈ TG
                </a>
              )}
              {cached?.website && (
                <a href={cached.website} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-[8px] font-bold text-purple-400 hover:bg-purple-500/20 transition-all">
                  🌐 Site
                </a>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <span className="font-mono text-[var(--color-fg-dim)] text-[9px]">1H VOL: {fmtUsd(token.vol1h)}</span>
            {/* Time Pills */}
            <span className="flex items-center gap-1 rounded-[2px] bg-indigo-500/10 px-1 py-0.5 text-[7px] font-bold text-indigo-400 border border-indigo-500/20" title="Time since creation">
              🕒 {formatDuration(Date.now() - (token.createdAt || token.firstSeenMs || Date.now()))}
            </span>
            <span className="flex items-center gap-1 rounded-[2px] bg-teal-500/10 px-1 py-0.5 text-[7px] font-bold text-teal-400 border border-teal-500/20" title="Time running (since bonded or created)">
              🏃 {formatDuration(Date.now() - (token.bondedMs || token.createdAt || token.firstSeenMs || Date.now()))}
            </span>
            {(token as any).hasCustomHooks && (
              <span className="flex items-center gap-1 rounded-full bg-rose-500/10 px-1 py-0.5 text-[7px] font-bold text-rose-500 border border-rose-500/30 shadow-[0_0_8px_rgba(244,63,94,0.4)] animate-pulse" title="This token uses custom, unaudited smart contract logic. Risk of honeypots or malicious code.">
                <ShieldAlert className="size-2" />
                UNAUDITED
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex-1 rounded-full bg-white/[0.05] overflow-hidden relative flex gap-0.5 h-1`}>
              <div
                className="h-full bg-gradient-to-r from-emerald-500 via-green-400 to-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.5)]"
                style={{ width: `${buyPct}%` }}
              />
              <div className="h-full bg-amber-500/40" style={{ width: "24%" }} />
            </div>
            <span className="font-mono text-[var(--color-fg-dim)] font-bold text-[9px]">24% TOP</span>
          </div>

          {/* Lily Quality Gate Stats Strip */}
          {nursery.isServerSideActive() && (
            <div className="flex items-center gap-2 mt-1.5 text-[9.5px] font-semibold border-t border-white/5 pt-1 w-full flex-wrap">
              <span className={devTone(token.creatorPct)}>
                <span className="opacity-50">Dev</span> {pctStr(token.creatorPct)}
              </span>
              <span className={t10Tone(token.holderTop10)}>
                <span className="opacity-50">T10</span> {pctStr(token.holderTop10)}
              </span>
              <span className={dipTone(token.dipPct)}>
                <span className="opacity-50">Dip</span> {token.dipPct != null ? `${token.dipPct.toLocaleString("en-US", {maximumFractionDigits: 0})}%` : '—'}
              </span>
              <span key={token.stale ? 'stale' : token.hidden ? 'warning' : !token.checked ? 'checking' : token.revived ? 'revival' : 'tradable'} className={`ml-auto px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border animate-in fade-in zoom-in duration-500 shadow-lg ${
                token.stale
                  ? "bg-white/5 text-[var(--color-fg-dim)] border-white/20"
                  : token.hidden
                  ? "bg-rose-500/10 text-rose-400 border-rose-500/50 shadow-rose-500/20"
                  : !token.checked
                  ? "bg-amber-500/10 text-amber-400 border-amber-500/50 shadow-amber-500/20 animate-pulse"
                  : token.revived
                  ? "bg-sky-500/10 text-sky-400 border-sky-500/50 shadow-sky-500/20"
                  : "bg-emerald-500/10 text-emerald-400 border-emerald-500/50 shadow-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.5)]"
              }`} title={token.hideReason || undefined}>
                {token.stale
                  ? "stale"
                  : token.hidden
                  ? `warning: ${token.hideReason || "gate"}`
                  : !token.checked
                  ? "checking…"
                  : token.revived
                  ? "revival"
                  : "tradable"}
              </span>
            </div>
          )}
        </div>

        {/* Top right: star button */}
        <div className={`absolute ${isMobile ? 'right-2 top-2' : 'right-4 top-4'} z-10`}>
          <button
            onClick={(e) => { e.stopPropagation(); }}
            className="p-2 rounded-full bg-black/40 border border-white/10 hover:bg-amber-500/20 hover:border-amber-500/30 transition-all hover:scale-110 hover:shadow-[0_0_20px_rgba(245,158,11,0.4)]"
          >
            <Star className="size-4 text-amber-400/50 hover:text-amber-400 transition-colors" />
          </button>
        </div>

        {/* Bottom left: image */}
        <div className="absolute left-4 bottom-4 z-10 flex items-end gap-1.5">
          <div className={`shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-black/40 shadow-[0_0_40px_rgba(0,0,0,0.8),0_0_20px_rgba(16,185,129,0.3)] ${mobileImageSize} ${imageAnimation}`}>
            {displayImage ? (
              <img src={displayImage} alt={displaySymbol} className="h-full w-full object-cover" loading="lazy" decoding="async" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[var(--color-fg-dim)]/20">
                <Zap className={isMobile ? 'size-4' : 'size-6'} />
              </div>
            )}
          </div>
          {token.creator && (
            <DeployerAvatar address={token.creator} size={isMobile ? "size-6" : "size-8"} />
          )}
        </div>

        {/* Bottom right: action buttons and other info */}
        <div className={`absolute ${isMobile ? 'left-20 right-4 bottom-4' : `${detailsLeftOffset} right-4 bottom-4`} z-10 flex flex-col gap-2 items-end`}>
          <div className="flex flex-col items-end gap-0.5 bg-black/60 px-2 py-1.5 rounded-xl border border-white/5 backdrop-blur-md">
            <div className="flex items-center gap-1.5">
              <span className="text-[8px] font-bold text-white/40 uppercase tracking-widest">MC</span>
              <span className={`font-mono font-black text-emerald-400 ${isMobile ? 'text-[11px]' : 'text-[12px]'}`}>{fmtUsd(token.marketCapUsd)}</span>
              <div className={`font-mono font-black ${(token.priceChange1h || 0) >= 0 ? "text-emerald-400" : "text-rose-400"} text-[9px]`}>
                {(token.priceChange1h || 0) >= 0 ? "+" : ""}{(token.priceChange1h || 0).toLocaleString("en-US", {maximumFractionDigits: 0})}%
              </div>
            </div>
            {(token.athMcapUsd || token.peakMcSol) > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-[8px] font-bold text-white/40 uppercase tracking-widest">ATH</span>
                <span className="font-mono font-black text-amber-400/80 text-[10px]">
                  {token.athMcapUsd ? fmtUsd(token.athMcapUsd) : fmtSol(token.peakMcSol)}
                </span>
              </div>
            )}
            {token.lastTradeMs > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-[8px] font-bold text-white/40">Last tx</span>
                <span className="font-mono text-[8px] text-white/40">{fmtAge(token.lastTradeMs)}</span>
              </div>
            )}
            <span className="font-mono text-white/30 text-[8px]">{fmtAge(token.bondedMs || token.firstSeenMs || Date.now())} ago</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); }}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 transition-all"
            >
              <ArrowUpRight className="size-3" />
              <span className="text-[10px] font-bold">{token.buys1h || 0}</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); }}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-rose-500/10 text-rose-400 transition-all"
            >
              <ArrowUpRight className="size-3 rotate-180" />
              <span className="text-[10px] font-bold">{token.sells1h || 0}</span>
            </button>
            {!isMobile && (
              <div className="flex items-center">
                {[10, 25, 50, 100].map((pct, index) => (
                  <button
                    key={pct}
                    onClick={(e) => { e.stopPropagation(); }}
                    className={`px-1.5 py-1 text-[9px] font-bold transition-all border border-emerald-500/20 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 ${
                      index === 0 ? 'rounded-l-lg' : index === 3 ? 'rounded-r-lg' : 'border-l-0'
                    }`}
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Runner row ────────────────────────────────────────────────────────────────

export function RunnerRow({ token, isSelected, onOpen }: {
  token: any; isSelected: boolean; onOpen: (mint: string) => void;
}) {
  const isMobile = useIsMobile();
  const cached = tokenMetaCache.get(token.mint);
  const displayImage = normalizeCoinImageUri(cached?.imageUri || token.imageUri, token.mint);
  const displayName = cached?.name || token.name || `Token ${token.mint.slice(0,4)}`;
  const displaySymbol = cached?.symbol || token.symbol || token.mint.slice(0,4).toUpperCase();

  const solUsd = 150;
  const currentMc = token.marketCapUsd || (token.marketCapSol ? token.marketCapSol * solUsd : 0);
  const peakMc = token.athMcapUsd || (token.peakMcSol ? token.peakMcSol * solUsd : currentMc);

  // Capped at size-20 so images never cover progress bars; still animated
  const imageSize = currentMc > 100000 ? 'size-20' : currentMc > 50000 ? 'size-16' : currentMc > 10000 ? 'size-14' : 'size-12';
  const mobileImageSize = isMobile ? 'size-16' : imageSize;

  const imageAnimation = "transition-all duration-700 ease-out hover:scale-110 hover:shadow-[0_0_30px_rgba(245,158,11,0.6)] animate-pulse-slow";

  const detailsLeftOffset = {
    'size-20': 'left-28',
    'size-16': 'left-24',
    'size-14': 'left-22',
    'size-12': 'left-20',
  }[imageSize] || 'left-24';

  return (
    <div
      role="button"
      tabIndex={0}
      onMouseEnter={() => prefetchCandles(token.mint, "1s")}
      onTouchStart={() => prefetchCandles(token.mint, "1s")}
      onClick={() => { prefetchCandles(token.mint, "1s"); onOpen(token.mint); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onOpen(token.mint); }}
      className={`group w-full border-b border-[var(--color-border-subtle)] ${isMobile ? 'px-3 py-2' : 'px-4 py-3'} text-left transition-all relative cursor-pointer outline-none hover:shadow-[0_0_30px_rgba(245,158,11,0.1)] hover:border-amber-500/20 ${
        isSelected ? "bg-amber-500/[0.04]" : "hover:bg-white/[0.02]"
      }`}
    >
      {isSelected && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500 shadow-[2px_0_10px_rgba(245,158,11,0.3)]" />
      )}

      {/* Chart takes full space as background */}
      <div className={`relative h-[280px]`}>
        <div className="absolute inset-0 bg-black/60 rounded-xl overflow-hidden border border-white/5 z-0">
          <MiniCandleChart
            mint={token.mint}
            height={280}
            rightOffset={isMobile ? 10 : 30}
            showLogo={false}
          />
        </div>

        {/* Top left: Info */}
        <div className={`absolute ${isMobile ? 'left-4 top-4 right-12' : 'left-4 top-4 max-w-[300px]'} z-10 flex flex-col gap-1`}>
          <div className="flex items-center gap-2 leading-none">
            <span className={`font-black bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 bg-clip-text text-transparent animate-gradient-x ${isMobile ? 'text-[13px]' : 'text-[16px]'}`}>{displaySymbol}</span>
            {Number(token.multiplier) > 0 && (
              <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-[3px] bg-amber-500/20 border border-amber-500/40 text-[11px] font-black text-amber-400 shadow-[0_0_15px_rgba(245,158,11,0.3)]">
                🔥 {Number(token.multiplier).toLocaleString("en-US", {minimumFractionDigits: 1, maximumFractionDigits: 1})}x
              </div>
            )}
            {computeUniversalHeat(token) > 0 && (
              <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-[3px] text-[10px] font-black border ${
                computeUniversalHeat(token) >= 75 ? 'bg-rose-500/20 text-rose-400 border-rose-500/40 shadow-[0_0_8px_rgba(244,63,94,0.4)]'
                : 'bg-orange-500/20 text-orange-400 border-orange-500/40'
              }`}>
                🏃 {computeUniversalHeat(token).toLocaleString()}
              </div>
            )}
          </div>
          <span className={`font-bold bg-gradient-to-r from-white/80 to-white/50 bg-clip-text text-transparent truncate ${isMobile ? 'text-[10px]' : 'text-[11px]'}`}>{displayName}</span>
          {cached?.description && (
            <span className={`font-sans text-white/50 leading-tight line-clamp-2 mt-0.5 max-w-[90%] ${isMobile ? 'text-[9px]' : 'text-[10px]'}`} title={cached.description}>
              {cached.description}
            </span>
          )}
          {!isMobile && (
            <span className="font-mono text-[8.5px] text-white/30 truncate select-all leading-none mt-1" title={token.mint}>
              {token.mint.slice(0, 6)}...{token.mint.slice(-6)}
            </span>
          )}
          {/* Runner narrative social links */}
          {!isMobile && (cached?.twitter || cached?.telegram || cached?.website) && (
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {cached?.twitter && (
                <a href={cached.twitter.startsWith('http') ? cached.twitter : `https://x.com/${cached.twitter.replace('@','')}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-[8px] font-bold text-sky-400 hover:bg-sky-500/20 transition-all">
                  𝕏 Twitter
                </a>
              )}
              {cached?.telegram && (
                <a href={cached.telegram.startsWith('http') ? cached.telegram : `https://t.me/${cached.telegram}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-[8px] font-bold text-blue-400 hover:bg-blue-500/20 transition-all">
                  ✈ TG
                </a>
              )}
              {cached?.website && (
                <a href={cached.website} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-[8px] font-bold text-purple-400 hover:bg-purple-500/20 transition-all">
                  🌐 Site
                </a>
              )}
            </div>
          )}
        </div>

        {/* Top right: star button */}
        <div className={`absolute ${isMobile ? 'right-2 top-2' : 'right-4 top-4'} z-10`}>
          <button
            onClick={(e) => { e.stopPropagation(); }}
            className="p-2 rounded-full bg-black/40 border border-white/10 hover:bg-amber-500/20 hover:border-amber-500/30 transition-all hover:scale-110 hover:shadow-[0_0_20px_rgba(245,158,11,0.4)]"
          >
            <Star className="size-4 text-amber-400/50 hover:text-amber-400 transition-colors" />
          </button>
        </div>

        {/* Bottom left: image */}
        <div className="absolute left-4 bottom-4 z-10 flex items-end gap-1.5">
          <div className={`shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-black/40 shadow-[0_0_40px_rgba(0,0,0,0.8),0_0_20px_rgba(245,158,11,0.3)] ${mobileImageSize} ${imageAnimation}`}>
            {displayImage ? (
              <img src={displayImage} alt={displaySymbol} className="h-full w-full object-cover" loading="lazy" decoding="async" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[var(--color-fg-dim)]/20">
                <Zap className={isMobile ? 'size-4' : 'size-6'} />
              </div>
            )}
          </div>
          {token.creator && (
            <DeployerAvatar address={token.creator} size={isMobile ? "size-6" : "size-8"} />
          )}
        </div>

        {/* Bottom right: stats */}
        <div className={`absolute ${isMobile ? 'left-20 right-4 bottom-4' : `${detailsLeftOffset} right-4 bottom-4`} z-10 flex flex-col gap-2 items-end`}>
          <div className="flex flex-col items-end gap-0.5 bg-black/60 px-2 py-1.5 rounded-xl border border-white/5 backdrop-blur-md">
            <div className="flex items-center gap-1.5">
              <span className="text-[8px] font-bold text-white/40 uppercase tracking-widest">MC</span>
              <span className={`font-mono font-black text-amber-400 ${isMobile ? 'text-[11px]' : 'text-[12px]'}`}>{fmtUsd(currentMc)}</span>
            </div>
            {peakMc > currentMc * 1.05 && (
              <div className="flex items-center gap-1.5">
                <span className="text-[8px] font-bold text-white/40 uppercase tracking-widest">ATH</span>
                <span className="font-mono font-black text-emerald-400/80 text-[10px]">{fmtUsd(peakMc)}</span>
              </div>
            )}
            {token.lastTradeMs > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-[8px] font-bold text-white/40">Last tx</span>
                <span className="font-mono text-[8px] text-white/40">{fmtAge(token.lastTradeMs)}</span>
              </div>
            )}
            <span className="font-mono text-white/30 text-[8px]">{fmtAge(token.bondedMs || token.firstSeenMs || Date.now())} ago</span>
          </div>
          {/* Buy/Sell pressure bar for runners */}
          {(token.buys1h + token.sells1h) > 0 && (
            <div className="flex items-center gap-1.5 w-full max-w-[140px]">
              <div className="flex-1 rounded-full bg-white/[0.05] overflow-hidden h-1">
                <div
                  className={`h-full bg-gradient-to-r from-emerald-500 to-green-400 shadow-[0_0_8px_rgba(16,185,129,0.5)]`}
                  style={{ width: `${Math.round((token.buys1h / (token.buys1h + token.sells1h)) * 100)}%` }}
                />
              </div>
              <span className={`font-mono font-bold text-[8px] ${
                Math.round((token.buys1h / (token.buys1h + token.sells1h)) * 100) >= 50 ? 'text-emerald-400' : 'text-rose-400'
              }`}>
                {Math.round((token.buys1h / (token.buys1h + token.sells1h)) * 100)}% B
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Grouped Token Row (Accordion) ─────────────────────────────────────────────────────────────

function DevProfileRow({
  profile,
  isSelected,
  onOpen,
  isExpanded,
  onToggleExpand,
  onConfigureRule,
  onBlacklist,
  onFilterCoins,
}: {
  profile: any;
  isSelected?: boolean;
  onOpen?: (mint: string) => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onConfigureRule?: (address: string) => void;
  onBlacklist?: (address: string) => void;
  onFilterCoins?: (address: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  const now = Date.now();

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!profile?.creator) return;
    navigator.clipboard.writeText(profile.creator);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const creator = profile.creator || "";
  const combinedAthSol = Number(profile.combinedAthSol) || 0;
  const bestAthSol = Number(profile.bestAthSol) || 0;
  const coinCount = Number(profile.coinCount) || (profile.tokens ? profile.tokens.length : 1);
  const winRate = Number(profile.winRate) || 100;
  const gradRate = Number(profile.gradRate) || 0;
  const totalWhales = Number(profile.totalWhales) || 0;
  const ruggedCount = Number(profile.ruggedCount) || 0;
  const tags: Array<{ id: string; label: string; color: string; icon?: string }> = profile.tags || [];

  // Determine left border strip color based on archetype/status
  const borderColor = 
    tags.some(t => t.id === "god_runner") ? "#a855f7" :
    tags.some(t => t.id === "predictable_pumper") ? "#f59e0b" :
    tags.some(t => t.id === "diamond" || t.id === "clean_record") ? "#10b981" :
    tags.some(t => t.id === "rug_risk" || t.id === "blacklisted" || t.id === "serial_rugger") ? "#f43f5e" :
    tags.some(t => t.id === "whale_magnet" || t.id === "whale_backed") ? "#06b6d4" :
    tags.some(t => t.id === "active_now") ? "#f59e0b" :
    "#6366f1";

  // Deterministic avatar gradient
  let hash = 0;
  for (let i = 0; i < creator.length; i++) {
    hash = creator.charCodeAt(i) + ((hash << 5) - hash);
  }
  const gradients = [
    "from-indigo-600/30 to-purple-600/30 border-indigo-500/40 text-indigo-300",
    "from-emerald-600/30 to-teal-600/30 border-emerald-500/40 text-emerald-300",
    "from-amber-600/30 to-orange-600/30 border-amber-500/40 text-amber-300",
    "from-cyan-600/30 to-blue-600/30 border-cyan-500/40 text-cyan-300",
    "from-pink-600/30 to-rose-600/30 border-pink-500/40 text-pink-300",
    "from-violet-600/30 to-fuchsia-600/30 border-violet-500/40 text-violet-300",
  ];
  const avatarGradient = gradients[Math.abs(hash) % gradients.length];
  const animDelay = (Math.abs(hash) * 137) % 2500;

  const truncate = (s: string, n = 4) => s ? `${s.slice(0, n)}...${s.slice(-n)}` : "";

  return (
    <div
      onClick={onToggleExpand}
      className={`relative border-b border-emerald-500/10 group/row cursor-pointer transition-colors ${
        isSelected ? "bg-emerald-950/20" : "hover:bg-emerald-950/15"
      }`}
      style={{
        animation: "sbt-ambient-shimmer 4.5s ease-in-out infinite",
        animationDelay: `${animDelay}ms`,
      }}
    >
      {/* Left PnL / Status color strip */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px] transition-colors duration-700"
        style={{ background: borderColor }}
      />

      {/* Row content: 2 rows strictly (Active Positions Layout) */}
      <div className="relative z-10 py-2 px-3 flex flex-col gap-1.5 text-[10px] font-mono">
        
        {/* ROW 1: Dev Identity + Copy + Behavioral Tags (Left) & Combined Peak ATH + Expand Chevron (Right) */}
        <div className="flex items-center justify-between gap-2 min-w-0">
          
          {/* Left: Avatar + Dev Identity + Badges */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Avatar */}
            <div className={`size-7 rounded-full bg-gradient-to-br border flex items-center justify-center shrink-0 ${avatarGradient} shadow-sm overflow-hidden`}>
              {profile.latestToken?.imageUri ? (
                <img
                  src={normalizeCoinImageUri(profile.latestToken.imageUri)}
                  alt="dev"
                  className="size-full rounded-full object-cover"
                  onError={(e) => { (e.currentTarget as HTMLElement).style.display = 'none'; }}
                />
              ) : (
                <User className="size-3.5" />
              )}
            </div>

            {/* Address & Badges */}
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
              <span
                className="font-black text-xs truncate text-white drop-shadow-sm"
                title={creator}
              >
                {truncate(creator, 4)}
              </span>

              {/* Copy Address Button */}
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 cursor-pointer text-white/40 hover:text-white group/btn bg-black/30 px-1 py-0.5 rounded text-[8px] shrink-0 border border-white/5 hover:border-white/20 transition-all"
                title="Copy Dev Address"
              >
                <span>{truncate(creator, 4)}</span>
                {copied ? (
                  <Check className="size-2 text-emerald-400" />
                ) : (
                  <Copy className="size-2 opacity-60 group-hover/btn:opacity-100" />
                )}
              </button>

              {/* Solscan Link */}
              <a
                href={`https://solscan.io/account/${creator}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-white/30 hover:text-blue-400 transition-colors shrink-0"
                title="View on Solscan"
              >
                <ExternalLink className="size-2.5" />
              </a>

              {/* Behavioral Tags Badges */}
              <div className="flex items-center gap-1 shrink-0 flex-wrap">
                {tags.slice(0, 3).map((tag) => {
                  let badgeClass = "bg-white/5 text-white/60 border-white/10";
                  if (tag.color === "emerald") badgeClass = "bg-emerald-500/10 text-emerald-400 border-emerald-500/25";
                  else if (tag.color === "purple") badgeClass = "bg-purple-500/10 text-purple-400 border-purple-500/25";
                  else if (tag.color === "blue") badgeClass = "bg-blue-500/10 text-blue-400 border-blue-500/25";
                  else if (tag.color === "cyan") badgeClass = "bg-cyan-500/10 text-cyan-400 border-cyan-500/25";
                  else if (tag.color === "amber") badgeClass = "bg-amber-500/10 text-amber-400 border-amber-500/25";
                  else if (tag.color === "rose") badgeClass = "bg-rose-500/10 text-rose-400 border-rose-500/25";
                  else if (tag.color === "indigo") badgeClass = "bg-indigo-500/10 text-indigo-400 border-indigo-500/25";

                  return (
                    <span
                      key={tag.id}
                      className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border ${badgeClass}`}
                    >
                      {tag.icon === "gem" && <Gem className="size-2" />}
                      {tag.icon === "flame" && <Flame className="size-2" />}
                      {tag.icon === "layers" && <Layers className="size-2" />}
                      {tag.icon === "shield" && <ShieldCheck className="size-2" />}
                      {tag.icon === "alert" && <ShieldAlert className="size-2" />}
                      {tag.icon === "crosshair" && <Target className="size-2" />}
                      {tag.icon === "zap" && <Zap className="size-2" />}
                      {tag.icon === "eye" && <Eye className="size-2" />}
                      {tag.icon === "ban" && <Ban className="size-2" />}
                      {tag.icon === "sparkles" && <Sparkles className="size-2" />}
                      {tag.icon === "repeat" && <Repeat className="size-2" />}
                      {tag.icon === "skull" && <Skull className="size-2" />}
                      {tag.label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: Combined Peak ATH & Deployed Badge */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex flex-col items-end">
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full font-black text-[10px] border bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                <TrendingUp className="size-2.5" />
                ${(combinedAthSol * 150).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <span className="text-[8px] font-mono text-white/40 mt-0.5 font-bold">
                {combinedAthSol.toFixed(1)} SOL Combined
              </span>
            </div>

            {/* Expand / Collapse Icon */}
            <div className={`p-1 rounded text-white/40 transition-transform ${isExpanded ? "rotate-180 text-emerald-400" : ""}`}>
              <ChevronDown className="size-3.5" />
            </div>
          </div>
        </div>

        {/* ROW 2: Compact Metrics Pill Strip & Action Buttons */}
        <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar no-scrollbar text-[9px] text-white/70 py-0.5">
          {/* Deployed Coins Pill */}
          <div className="flex items-center gap-1 bg-indigo-500/10 px-1.5 py-0.5 rounded border border-indigo-500/20 text-indigo-300 font-bold shrink-0">
            <Layers className="size-2.5 text-indigo-400" />
            <span className="text-indigo-400/60 font-normal">Deployed:</span>
            <span>{coinCount} {coinCount === 1 ? 'Coin' : 'Coins'}</span>
          </div>

          {/* Top ATH Pill */}
          <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0">
            <Flame className="size-2.5 text-amber-400" />
            <span className="text-white/40">Top ATH:</span>
            <span className="text-emerald-300 font-bold">{bestAthSol.toFixed(1)} SOL</span>
            <span className="text-white/30">(${((bestAthSol * 150)).toLocaleString(undefined, { maximumFractionDigits: 0 })})</span>
          </div>

          {/* Clean Record / Win Rate Pill */}
          <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0">
            <ShieldCheck className="size-2.5 text-emerald-400" />
            <span className="text-white/40">Clean Record:</span>
            <span className={`font-bold ${ruggedCount === 0 ? 'text-emerald-300' : 'text-amber-300'}`}>
              {ruggedCount === 0 ? '100%' : `${winRate}% (${ruggedCount} Rugs)`}
            </span>
          </div>

          {/* Playbook Metrics: Pump Rate */}
          {profile.playbook && profile.playbook.pumpRate > 0 && (
            <div className="flex items-center gap-1 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20 text-amber-300 font-bold shrink-0" title="Playbook: % of launches pumping >= 1.5x (~45 SOL) before dumping">
              <Zap className="size-2.5 text-amber-400" />
              <span className="text-amber-400/60 font-normal">Pump Rate:</span>
              <span>{profile.playbook.pumpRate}%</span>
            </div>
          )}

          {/* Playbook Metrics: Avg Multiplier */}
          {profile.playbook && profile.playbook.avgMultiplier > 1 && (
            <div className="flex items-center gap-1 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 text-emerald-300 font-bold shrink-0" title="Average multiplier achieved on bonding curve">
              <TrendingUp className="size-2.5 text-emerald-400" />
              <span className="text-emerald-400/60 font-normal">Avg Run:</span>
              <span>{profile.playbook.avgMultiplier}x</span>
            </div>
          )}

          {/* Grad Rate Pill (if any grads) */}
          {gradRate > 0 && (
            <div className="flex items-center gap-1 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 text-emerald-300 font-bold shrink-0">
              <span className="text-emerald-400/60 font-normal">Grad Rate:</span>
              <span>{gradRate}%</span>
            </div>
          )}

          {/* Whales Pill */}
          {totalWhales > 0 && (
            <div className="flex items-center gap-1 bg-cyan-500/10 px-1.5 py-0.5 rounded border border-cyan-500/20 text-cyan-300 font-bold shrink-0">
              <span>🐋 {totalWhales} Whale Buys</span>
            </div>
          )}

          {/* Latest Coin */}
          {profile.latestToken && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (onOpen && profile.latestToken.mint) onOpen(profile.latestToken.mint);
              }}
              className="flex items-center gap-1 bg-white/[0.04] hover:bg-white/10 px-1.5 py-0.5 rounded border border-white/5 shrink-0 cursor-pointer transition-colors"
              title="Click to view latest coin chart"
            >
              <span className="text-white/40">Latest:</span>
              <span className="text-emerald-300 font-bold hover:underline">${profile.latestToken.symbol}</span>
            </div>
          )}

          {/* Last Active */}
          <div className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded border border-white/5 shrink-0">
            <Clock className="size-2.5 text-blue-400" />
            <span>{profile.lastSeenMs ? formatDuration(now - profile.lastSeenMs) + ' ago' : 'Recent'}</span>
          </div>

          {/* Trader Action Buttons */}
          <div className="flex items-center gap-1.5 shrink-0 ml-auto">
            {/* Quick Snipe & Flip button */}
            {onConfigureRule && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onConfigureRule(creator);
                }}
                className="flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 hover:border-amber-500/60 text-[8px] font-bold text-amber-300 transition-all shrink-0 cursor-pointer shadow-sm hover:scale-105"
                title="Configure Front-Run Quick-Flip (+50% TP, -20% SL, Dump Shield)"
              >
                <Target className="size-2.5 text-amber-400" />
                <span>Snipe & Flip</span>
              </button>
            )}

            {/* Blacklist button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onBlacklist) onBlacklist(creator);
              }}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 hover:border-rose-500/40 text-[8px] font-bold text-rose-300 transition-colors shrink-0 cursor-pointer"
              title="Blacklist dev from all bot & manual trades"
            >
              <Ban className="size-2.5 text-rose-400" />
              <span>Blacklist</span>
            </button>

            {/* Coins filter button */}
            {onFilterCoins && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onFilterCoins(creator);
                }}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 hover:bg-white/10 border border-white/10 text-[8px] text-white/70 hover:text-white transition-colors shrink-0 cursor-pointer"
                title="Search database for all tokens by this dev"
              >
                <Search className="size-2 text-cyan-400" />
                <span>Coins ({coinCount})</span>
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

function GroupedTokenRow({
  group,
  tab,
  caMintInput,
  onOpenToken,
  onConfigureRule,
  onFilterDev,
}: {
  group: any;
  tab: Tab;
  caMintInput: string;
  onOpenToken: (mint: string) => void;
  onConfigureRule?: (address: string) => void;
  onFilterDev?: (devWallet: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  
  const isSelected = caMintInput === group.main.mint;
  const RowComponent = (tab === "new" || tab === "grad") ? PrebondRow : (tab === "runners") ? RunnerRow : BondedRow;

  return (
    <div className="relative border-b border-[var(--color-border-subtle)]">
      <div className="relative z-10 bg-[var(--color-bg-editor)]">
        {tab === "devs" ? (
          <DevProfileRow
            profile={group.main}
            isSelected={isSelected}
            onOpen={onOpenToken}
            isExpanded={expanded}
            onToggleExpand={() => setExpanded(!expanded)}
            onConfigureRule={onConfigureRule}
            onBlacklist={(addr) => {
              deployerStore.setRule(addr, { isBlacklisted: true, isAutoSnipe: false });
            }}
            onFilterCoins={onFilterDev}
          />
        ) : (
          <RowComponent
            token={group.main}
            showProgress={tab === "grad" || tab === "my"}
            showScore={tab === "old"}
            isSelected={isSelected}
            onOpen={onOpenToken}
          />
        )}
      </div>
      
      {tab === "my" && (
        <div className="bg-gradient-to-b from-indigo-900/20 to-black/40 border-t border-indigo-500/20 p-4 pt-3 flex flex-col gap-3">
          <div className="flex items-center gap-2 mb-1">
            <div className="size-5 rounded bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
              <Zap className="size-3 text-indigo-400" />
            </div>
            <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Deployer Intelligence</span>
            <span className="ml-auto text-[9px] font-mono font-bold text-white/40 flex items-center gap-1">
              <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" /></span> Live
            </span>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-black/30 rounded-lg border border-white/5 p-2.5 flex flex-col gap-1">
              <span className="text-[8px] font-bold text-white/40 uppercase tracking-wider flex items-center gap-1"><User className="size-2.5" /> Retention Rate</span>
              <span className="text-[14px] font-mono font-black text-emerald-400">84.2%</span>
            </div>
            <div className="bg-black/30 rounded-lg border border-white/5 p-2.5 flex flex-col gap-1">
              <span className="text-[8px] font-bold text-white/40 uppercase tracking-wider flex items-center gap-1"><ShieldAlert className="size-2.5" /> Sniper Detection</span>
              <span className="text-[14px] font-mono font-black text-amber-400">2 Blocked</span>
            </div>
            <div className="bg-black/30 rounded-lg border border-white/5 p-2.5 flex flex-col gap-1">
              <span className="text-[8px] font-bold text-white/40 uppercase tracking-wider flex items-center gap-1"><User className="size-2.5" /> Top 5 Holdings</span>
              <span className="text-[14px] font-mono font-black text-blue-400">42%</span>
            </div>
          </div>
        </div>
      )}

      {tab !== "my" && tab !== "devs" && group.others.length > 0 && (
        <div className="absolute right-4 bottom-2 z-20">
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all shadow-lg backdrop-blur-sm ${
              expanded 
                ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-400" 
                : "bg-indigo-500/20 border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/30 hover:scale-105"
            }`}
          >
            <Layers className="size-3" />
            <span className="text-[9px] font-black uppercase tracking-widest">
              +{group.others.length} More
            </span>
            {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          </button>
        </div>
      )}

      {tab !== "my" && expanded && group.others.length > 0 && (
        <div className="bg-black/30 animate-in fade-in slide-in-from-top-2 relative z-0">
          <div className="absolute left-6 top-0 bottom-0 w-px bg-indigo-500/20 z-0" />
          <div className="pl-4 border-t border-[var(--color-border-subtle)]">
            {tab === "devs" && (
              <div className="py-1 px-3 bg-indigo-950/40 border-b border-indigo-500/20 flex items-center justify-between text-[9px] font-mono text-indigo-300">
                <span className="font-bold uppercase tracking-wider flex items-center gap-1.5">
                  <Layers className="size-2.5 text-indigo-400" />
                  All Deployments ({group.others.length})
                </span>
                <span className="text-white/40 text-[8px]">Click any coin to view live chart & tape</span>
              </div>
            )}
            {group.others.map((token: any) => {
              const ChildRow = tab === "devs" ? PrebondRow : RowComponent;
              return (
                <ChildRow 
                  key={token.mint} 
                  token={token} 
                  showProgress={tab === "grad"} 
                  showScore={tab === "old"} 
                  isSelected={caMintInput === token.mint} 
                  onOpen={onOpenToken} 
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Lily Row ──────────────────────────────────────────────────────────────────

function LilyGridRow({ groups, caMintInput, onOpenToken }: { groups: any[], caMintInput: string, onOpenToken: (mint: string) => void }) {
  return (
    <div className="flex w-full px-2 py-1 gap-2 overflow-hidden items-center justify-start h-[120px]">
      {groups.map((group) => {
        const token = group.main;
        const isSelected = caMintInput === token.mint;
        const cached = tokenMetaCache.get(token.mint);
        const displayImage = normalizeCoinImageUri(token.imageUri || cached?.imageUri, token.mint);
        const displaySymbol = token.symbol || cached?.symbol || token.mint.slice(0, 4).toUpperCase();
        
        return (
          <div
            key={group.groupKey}
            onMouseEnter={() => prefetchCandles(token.mint, "1s")}
            onTouchStart={() => prefetchCandles(token.mint, "1s")}
            onClick={() => { prefetchCandles(token.mint, "1s"); onOpenToken(token.mint); }}
            className={`flex-1 min-w-0 max-w-[140px] h-full relative flex flex-col items-center justify-center p-2 rounded-xl border bg-black/40 cursor-pointer transition-all hover:scale-105 hover:z-10 ${
              isSelected ? "border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.2)] bg-emerald-500/10" : "border-white/5 hover:border-emerald-500/30"
            }`}
          >
            <div className="relative mb-2">
              <img src={displayImage || undefined} className="size-12 md:size-14 rounded-full border border-white/10 object-cover bg-black/50 shadow-md" />
              {token.creator && (
                <div className="absolute -bottom-1 -right-1 z-20">
                  <DeployerAvatar address={token.creator} size="size-5 md:size-6" />
                </div>
              )}
            </div>
            <span className="text-[10px] font-black text-white truncate w-full text-center leading-none">{displaySymbol}</span>
            <span className="text-[9px] font-mono text-emerald-400 mt-1 leading-none">{fmtSol(token.marketCapSol)}</span>
            {(token.hidden || !token.checked) && (
               <div className="absolute top-1 right-1 size-2 rounded-full bg-rose-500/80 animate-pulse" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function NurseryPanel({ onOpenToken, defaultTab = "bonded", forceTab }: { onOpenToken: (mint: string) => void; defaultTab?: Tab; forceTab?: Tab }) {
  const isMobile = useIsMobile();
  const lastScrollY = useRef(0);

  // Always reset chrome visibility when leaving Nursery
  useEffect(() => {
    return () => {
      document.body.classList.remove("mobile-chrome-hidden");
    };
  }, []);

  const { caMintInput, setCaMintInput, layoutMode, openDeployModal, presavedGraphs } = useApp() as any;
  const { t } = useTranslation();

  const handleOpenToken = useCallback((mint: string) => {
    prefetchCandles(mint, "1s");
    if (onOpenToken) {
      onOpenToken(mint);
    } else {
      setCaMintInput(mint);
    }
    window.dispatchEvent(new CustomEvent("open-token", { detail: mint }));
  }, [onOpenToken, setCaMintInput]);

  const [tabState, setTab] = useState<Tab>(defaultTab);
  const tab = forceTab || tabState;
  const [snap, setSnap] = useState<NurserySnapshot>(() => nursery.getSnapshot());
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [ruleTargetAddress, setRuleTargetAddress] = useState<string>("");
  const [devWalletFilter, setDevWalletFilter] = useState<string | undefined>(undefined);
  const [nurserySearch, setNurserySearch] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dev = params.get("devWallet");
    if (dev) {
      setDevWalletFilter(dev);
      setTab("devs");
    }
  }, []);

  const walletPk = getPumpPortalTradingWalletPubkey();

  // Sub-tab state hooks
  const [newSubTab, setNewSubTab] = useState<'unchecked' | 'checked' | 'tradable' | 'blocked'>('unchecked');
  const [bondSubTab, setBondSubTab] = useState<'unchecked' | 'tradable' | 'blocked' | 'stale'>('tradable');

  // Sorting state hooks
  const [sortKey, setSortKey] = useState<'age' | 'mc' | 'vol' | 'heat'>(defaultTab === 'new' ? 'age' : 'heat');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    nursery.start().then(() => setSnap(nursery.getSnapshot()));
    const id = setInterval(() => setSnap(nursery.getSnapshot()), 2000);
    return () => clearInterval(id);
  }, []);

  // Background prefetch top visible Nursery tokens to ensure 0ms instant chart loading
  useEffect(() => {
    if (!snap?.visibleTokens || snap.visibleTokens.length === 0) return;
    const topMints = snap.visibleTokens.slice(0, 10).map((t: any) => t.mint).filter(Boolean);
    if (topMints.length > 0) {
      prefetchBatchCandles(topMints, "1s", 8);
    }
  }, [snap?.visibleTokens, tab]);

  const [isRehydrating, setIsRehydrating] = useState(false);
  const handleRehydrate = useCallback(async () => {
    setIsRehydrating(true);
    try {
      await nursery.rehydrate();
      setSnap(nursery.getSnapshot());
    } finally {
      setIsRehydrating(false);
    }
  }, []);

  const myDeploymentsList = useMemo(() => {
    const presaved = (presavedGraphs || []).map((g: any) => ({
      mint: g.name, // using name as id
      name: g.name,
      symbol: (g.name || "Draft").substring(0, 4).toUpperCase(),
      creator: walletPk || "local", 
      marketCapSol: 0,
      marketCapUsd: 0,
      isPresaved: true,
      firstSeenMs: Date.now(),
      priceChange1h: 0,
      buys1h: 0,
      sells1h: 0,
      vol1h: 0,
      bondedMs: Date.now()
    }));

    if (!walletPk) return presaved;
    const all = snap.visibleTokens || [];
    // Filter out duplicates and match creator
    const seen = new Set<string>();
    const deployed = all.filter(t => {
      if (seen.has(t.mint)) return false;
      seen.add(t.mint);
      return t.creator === walletPk;
    });
    
    return [...presaved, ...deployed];
  }, [walletPk, snap, presavedGraphs]);

  const counts = {
    my: myDeploymentsList.length,
    new: snap.counts?.new?.total || 0,
    grad: snap.counts?.grad?.total || 0,
    bonded: snap.counts?.bonded?.total || 0,
    old: 0,
    runners: snap.counts?.runners?.total || 0,
    devs: (snap.counts as any)?.devs?.total || 0,
  };

  // Pre-calculate sub-tab counts
  const newBlockedCount = snap.counts?.new?.blocked || 0;
  const newTradableCount = snap.counts?.new?.tradable || 0;
  const newCheckedCount = snap.counts?.new?.checked || 0;
  const newUncheckedCount = snap.counts?.new?.unchecked || 0;

  const bondStaleCount = snap.counts?.bonded?.stale || 0;
  const bondBlockedCount = snap.counts?.bonded?.blocked || 0;
  const bondTradableCount = snap.counts?.bonded?.tradable || 0;
  const bondUncheckedCount = snap.counts?.bonded?.unchecked || 0;

  const handleBondedClick = () => {
    setTab("bonded");
    const arr = [
      { key: 'tradable', val: bondTradableCount },
      { key: 'stale', val: bondStaleCount },
      { key: 'unchecked', val: bondUncheckedCount },
      { key: 'blocked', val: bondBlockedCount }
    ];
    arr.sort((a, b) => b.val - a.val);
    if (arr[0].val > 0) {
      setBondSubTab(arr[0].key as any);
    }
  };

  // Trigger fetch when parameters change
  useEffect(() => {
    if (tab === "my") return;
    const sub = tab === "bonded" ? bondSubTab : (tab === "new" || tab === "grad" ? newSubTab : "");
    const q = nurserySearch.trim();
    nursery.fetchPage(tab, sub, sortKey, sortDir, 0, 1500, false, devWalletFilter, q || undefined).then(() => {
      setSnap(nursery.getSnapshot());
    });
  }, [tab, newSubTab, bondSubTab, sortKey, sortDir, devWalletFilter, nurserySearch]);

  const highestRunToken = useMemo(() => {
    if (tab !== "runners" || snap.visibleTokens.length === 0) return null;
    let max = 0;
    let maxT = null;
    for (const t of snap.visibleTokens) {
      const m = Number(t.multiplier) || 0;
      if (m > max) {
        max = m;
        maxT = t;
      }
    }
    return maxT;
  }, [tab, snap.visibleTokens]);



  const baseSortedList = tab === "my" ? myDeploymentsList : snap.visibleTokens;
  const sortedList = useMemo(() => {
    if (!nurserySearch.trim()) return baseSortedList;
    const q = nurserySearch.trim().toLowerCase();
    return baseSortedList.filter((t: any) => {
      const sym = (t.symbol || "").toLowerCase();
      const name = (t.name || "").toLowerCase();
      const mint = (t.mint || "").toLowerCase();
      const creator = (t.creator || t.deployer || "").toLowerCase();
      return sym.includes(q) || name.includes(q) || mint.includes(q) || creator.includes(q);
    });
  }, [baseSortedList, nurserySearch]);

  const groupedList = useMemo(() => {
    interface TokenGroup {
      main: any;
      others: any[];
      groupKey: string;
    }
    const groups: TokenGroup[] = [];
    if (tab === "devs") {
      for (const dev of sortedList) {
        groups.push({
          main: dev,
          others: dev.tokens || [],
          groupKey: dev.creator
        });
      }
      return groups;
    }
    for (const token of sortedList) {
      const cached = tokenMetaCache.get(token.mint);
      const name = token.name || cached?.name;
      const creator = token.creator;

      const existingGroup = groups.find(g => {
        if (creator && g.main.creator === creator) return true;
        const gCached = tokenMetaCache.get(g.main.mint);
        const gName = g.main.name || gCached?.name;
        if (name && gName && gName === name) return true;
        return false;
      });

      if (existingGroup) {
        // Swap main if incoming token has higher market cap
        const incomingMc = (token as any).marketCapSol || (token as any).marketCapUsd || 0;
        const mainMc = (existingGroup.main as any).marketCapSol || (existingGroup.main as any).marketCapUsd || 0;

        if (incomingMc > mainMc) {
          existingGroup.others.push(existingGroup.main);
          existingGroup.main = token;
        } else {
          existingGroup.others.push(token);
        }
      } else {
        groups.push({ main: token, others: [], groupKey: token.mint });
      }
    }
    return groups;
  }, [sortedList]);

  const cols = isMobile ? 4 : 8;
  const virtualItemsList = useMemo(() => {
    if (layoutMode === "lily") {
      const rows = [];
      for (let i = 0; i < groupedList.length; i += cols) {
        rows.push(groupedList.slice(i, i + cols));
      }
      return rows;
    }
    return groupedList.filter(token => {
      // Filter out blacklisted devs
      const history = deployerStore.getHistory(token.main.creator);
      if (history?.isBlacklisted) return false;
      return true;
    });
  }, [groupedList, layoutMode, cols]);

  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: virtualItemsList.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => layoutMode === "lily" ? 120 : (tab === "devs" ? 72 : 280),
    overscan: 4,
  });

  const isFetchingMore = useRef(false);
  const virtualItems = rowVirtualizer.getVirtualItems();
  const lastItem = virtualItems[virtualItems.length - 1];
  const totalItems = virtualItemsList.length;

  useEffect(() => {
    if (!lastItem || totalItems === 0 || isFetchingMore.current || tab === "my") return;
    
    const maxCount = snap.visibleTotal;
    if (maxCount && snap.visibleTokens.length >= maxCount) return;

    if (lastItem.index >= totalItems - (layoutMode === "lily" ? 10 : 30)) {
      isFetchingMore.current = true;
      const sub = tab === "bonded" ? bondSubTab : (tab === "new" || tab === "grad" ? newSubTab : "");
      const q = nurserySearch.trim();
      nursery.fetchPage(tab, sub, sortKey, sortDir, snap.visibleTokens.length, 1500, true, devWalletFilter, q || undefined).finally(() => {
        setSnap(nursery.getSnapshot());
        isFetchingMore.current = false;
      });
    }
  }, [lastItem?.index, totalItems, tab, newSubTab, bondSubTab, sortKey, sortDir, snap.visibleTotal, snap.visibleTokens.length, devWalletFilter, nurserySearch, layoutMode]);

  return (
    <div className={`flex h-full min-h-0 flex-col relative ${isMobile ? "bg-transparent" : "bg-[var(--color-bg-editor)]"}`}>

      {isMobile || forceTab ? null : (
        <div className="flex border-b border-white/5 shrink-0 z-10 backdrop-blur-md bg-neutral-900/95 w-full overflow-hidden">
          <TabButton active={tab === "my"} onClick={() => setTab("my")} count={counts.my} flashDelay={0}>{t("nursery.tab.yours")}</TabButton>
          <TabButton active={tab === "new"} onClick={() => { setTab("new"); setSortKey("age"); setSortDir("desc"); }} count={counts.new} flashDelay={150}>{t("nursery.tab.new")}</TabButton>
          <TabButton active={tab === "grad"} onClick={() => setTab("grad")} count={counts.grad} flashDelay={300}>{t("nursery.tab.grad")}</TabButton>
          <TabButton active={tab === "bonded"} onClick={handleBondedClick} count={counts.bonded} flashDelay={450}>{t("nursery.tab.bonded")}</TabButton>

          <TabButton active={tab === "runners"} onClick={() => setTab("runners")} count={counts.runners} flashDelay={750}>{t("nursery.tab.runners")}</TabButton>
          <TabButton active={tab === "devs"} onClick={() => setTab("devs")} count={counts.devs} flashDelay={900}>{t("nursery.tab.devs")}</TabButton>

          <button 
            onClick={() => setIsRulesOpen(true)}
            className="ml-auto shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-black uppercase text-amber-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors border-l border-white/5"
            title="Dev Sniper & Blacklist Rules"
          >
            <ShieldCheck className="size-3.5" />
            <span className="hidden xl:inline">Rules</span>
          </button>
        </div>
      )}

      <div 
        ref={parentRef}
        className={`flex-1 overflow-y-auto custom-scrollbar relative ${isMobile ? "pb-[100px]" : ""}`}
        onScroll={(e) => {
          if (!isMobile) return;
          const currentScrollY = e.currentTarget.scrollTop;
          if (Math.abs(currentScrollY - lastScrollY.current) < 10) return;
          
          const isScrollingDown = currentScrollY > lastScrollY.current && currentScrollY > 60;
          if (isScrollingDown) {
            document.body.classList.add("mobile-chrome-hidden");
          } else {
            document.body.classList.remove("mobile-chrome-hidden");
          }
          lastScrollY.current = currentScrollY;
        }}
      >
        {isMobile && <div className="h-14 shrink-0" />}

        {isMobile && (
          <div className="flex border-b border-white/5 shrink-0 sticky top-0 z-30 backdrop-blur-xl bg-neutral-900/60 w-full overflow-hidden">
            <TabButton active={tab === "my"} onClick={() => setTab("my")} count={counts.my} flashDelay={0}>{t("nursery.tab.yours")}</TabButton>
            <TabButton active={tab === "new"} onClick={() => { setTab("new"); setSortKey("age"); setSortDir("desc"); }} count={counts.new} flashDelay={150}>{t("nursery.tab.new")}</TabButton>
            <TabButton active={tab === "grad"} onClick={() => setTab("grad")} count={counts.grad} flashDelay={300}>{t("nursery.tab.grad")}</TabButton>
            <TabButton active={tab === "bonded"} onClick={handleBondedClick} count={counts.bonded} flashDelay={450}>{t("nursery.tab.bonded")}</TabButton>

            <TabButton active={tab === "runners"} onClick={() => setTab("runners")} count={counts.runners} flashDelay={750}>{t("nursery.tab.runners")}</TabButton>
            <TabButton active={tab === "devs"} onClick={() => setTab("devs")} count={counts.devs} flashDelay={900}>{t("nursery.tab.devs")}</TabButton>
          </div>
        )}

        {/* Sub-tabs and Sort Controls Strip */}
        <div className={`flex flex-col border-b border-[var(--color-border-subtle)] px-4 py-2 gap-2 shrink-0 ${isMobile ? "sticky top-[48px] z-20 backdrop-blur-xl bg-neutral-900/60" : "bg-black/10"}`}>
          {/* Render sub-tabs if tab is new, grad, or bonded */}
          {(tab === "new" || tab === "grad" || tab === "bonded") && (
            <div className="flex gap-1.5 overflow-x-auto pb-1 custom-scrollbar">
              {(tab === "new" || tab === "grad") ? (
                <>
                  <button
                    onClick={() => setNewSubTab('tradable')}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold border transition-all ${
                      newSubTab === 'tradable'
                        ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400 font-black'
                        : 'bg-white/5 border-white/10 text-[var(--color-fg-dim)] hover:bg-white/10'
                    }`}
                  >
                    {t("nursery.sub.tradable")} <span className="opacity-60 font-mono">({newTradableCount})</span>
                  </button>
                  <button
                    onClick={() => setNewSubTab('checked')}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold border transition-all ${
                      newSubTab === 'checked'
                        ? 'bg-sky-500/20 border-sky-500/40 text-sky-400 font-black'
                        : 'bg-white/5 border-white/10 text-[var(--color-fg-dim)] hover:bg-white/10'
                    }`}
                  >
                    {t("nursery.sub.checked")} <span className="opacity-60 font-mono">({newCheckedCount})</span>
                  </button>
                  <button
                    onClick={() => setNewSubTab('blocked')}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold border transition-all ${
                      newSubTab === 'blocked'
                        ? 'bg-rose-500/20 border-rose-500/40 text-rose-400 font-black'
                        : 'bg-white/5 border-white/10 text-[var(--color-fg-dim)] hover:bg-white/10'
                    }`}
                  >
                    {t("nursery.sub.blocked")} <span className="opacity-60 font-mono">({newBlockedCount})</span>
                  </button>
                  <button
                    onClick={() => setNewSubTab('unchecked')}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold border transition-all ${
                      newSubTab === 'unchecked'
                        ? 'bg-amber-500/20 border-amber-500/40 text-amber-400 font-black'
                        : 'bg-white/5 border-white/10 text-[var(--color-fg-dim)] hover:bg-white/10'
                    }`}
                  >
                    {t("nursery.sub.unchecked")} <span className="opacity-60 font-mono">({newUncheckedCount})</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setBondSubTab('tradable')}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold border transition-all ${
                      bondSubTab === 'tradable'
                        ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400 font-black'
                        : 'bg-white/5 border-white/10 text-[var(--color-fg-dim)] hover:bg-white/10'
                    }`}
                  >
                    {t("nursery.sub.tradable")} <span className="opacity-60 font-mono">({bondTradableCount})</span>
                  </button>
                  <button
                    onClick={() => setBondSubTab('blocked')}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold border transition-all ${
                      bondSubTab === 'blocked'
                        ? 'bg-rose-500/20 border-rose-500/40 text-rose-400 font-black'
                        : 'bg-white/5 border-white/10 text-[var(--color-fg-dim)] hover:bg-white/10'
                    }`}
                  >
                    {t("nursery.sub.blocked")} <span className="opacity-60 font-mono">({bondBlockedCount})</span>
                  </button>
                  <button
                    onClick={() => setBondSubTab('unchecked')}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold border transition-all ${
                      bondSubTab === 'unchecked'
                        ? 'bg-amber-500/20 border-amber-500/40 text-amber-400 font-black'
                        : 'bg-white/5 border-white/10 text-[var(--color-fg-dim)] hover:bg-white/10'
                    }`}
                  >
                    {t("nursery.sub.unchecked")} <span className="opacity-60 font-mono">({bondUncheckedCount})</span>
                  </button>
                  <button
                    onClick={() => setBondSubTab('stale')}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold border transition-all ${
                      bondSubTab === 'stale'
                        ? 'bg-blue-500/20 border-blue-500/40 text-blue-400 font-black'
                        : 'bg-white/5 border-white/10 text-[var(--color-fg-dim)] hover:bg-white/10'
                    }`}
                  >
                    {t("nursery.sub.stale")} <span className="opacity-60 font-mono">({bondStaleCount})</span>
                  </button>
                </>
              )}
            </div>
          )}
  
          {/* Sort Controls Strip */}
          <div className="flex items-center justify-between text-[11px] gap-2 flex-wrap sm:flex-nowrap">
            <div className="flex items-center gap-1.5 text-[var(--color-fg-dim)] shrink-0">
              <span>{t("nursery.sort.label")}</span>
              <div className="flex bg-black/40 rounded-lg p-0.5 border border-white/5">
                {(tab === "devs" ? ['coins', 'ath', 'winRate', 'active'] : ['heat', 'age', 'mc', 'vol']).map((k) => (
                  <button
                    key={k}
                    onClick={() => setSortKey(k as any)}
                    className={`px-2 py-0.5 rounded text-[10px] font-bold capitalize transition-all ${
                      sortKey === k || (tab === "devs" && sortKey === 'heat' && k === 'coins') // fallback active state
                        ? 'bg-emerald-500/20 text-emerald-400 font-black'
                        : 'text-[var(--color-fg-dim)] hover:text-white'
                    }`}
                  >
                    {k === 'coins' ? t('nursery.sort.coins') : k === 'ath' ? t('nursery.sort.ath') : k === 'winRate' ? 'Clean' : k === 'active' ? 'Recent' : k === 'vol' ? t('nursery.sort.vol') : k === 'mc' ? t('nursery.sort.mc') : k === 'heat' ? t('nursery.sort.heat') : t('nursery.sort.age')}
                  </button>
                ))}
              </div>
            </div>

            {/* In-view Search Filter */}
            <div className="relative flex-1 max-w-[240px] sm:max-w-xs min-w-[140px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-white/40" />
              <input
                type="text"
                value={nurserySearch}
                onChange={(e) => setNurserySearch(e.target.value)}
                placeholder={t("nursery.search.placeholder")}
                className="w-full bg-black/40 border border-white/10 rounded-lg pl-7 pr-6 py-1 text-[11px] text-white placeholder:text-white/30 focus:outline-none focus:border-emerald-500/50"
              />
              {nurserySearch && (
                <button 
                  onClick={() => setNurserySearch("")} 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                  title="Clear search"
                >
                  <X className="size-3" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              {/* Rehydrate from DB button */}
              <button
                onClick={handleRehydrate}
                disabled={isRehydrating}
                title="Reload all tokens from database"
                className={`flex items-center gap-1 px-2 py-1 rounded border transition-all text-[10px] ${
                  isRehydrating
                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 cursor-wait'
                    : 'border-white/10 bg-white/5 text-[var(--color-fg-dim)] hover:text-emerald-400 hover:border-emerald-500/30 hover:bg-emerald-500/10'
                }`}
              >
                <RefreshCw className={`size-3 ${isRehydrating ? 'animate-spin' : ''}`} />
                <span>{isRehydrating ? 'Loading…' : 'DB Sync'}</span>
              </button>

              <button
                onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="flex items-center gap-1 px-2 py-1 rounded border border-white/10 bg-white/5 text-[var(--color-fg-dim)] hover:text-white transition-all text-[10px]"
                title={sortDir === 'desc' ? 'Descending' : 'Ascending'}
              >
                <span>{sortDir === 'desc' ? t('nursery.sort.highToLow') : t('nursery.sort.lowToHigh')}</span>
                <span>{sortDir === 'desc' ? '↓' : '↑'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Hero Section for Highest Runner */}
        {tab === "runners" && highestRunToken && (
          <div 
            onMouseEnter={() => prefetchCandles(highestRunToken.mint, "1s")}
            onTouchStart={() => prefetchCandles(highestRunToken.mint, "1s")}
            onClick={() => handleOpenToken(highestRunToken.mint)}
            className="mx-4 my-3 p-5 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-600/10 border border-amber-500/20 shadow-[0_0_40px_rgba(245,158,11,0.15)] relative overflow-hidden cursor-pointer group hover:border-amber-500/40 transition-all"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
            
            <div className="relative z-10 flex flex-col md:flex-row gap-5">
              {/* Large Image */}
              <div className="size-20 md:size-28 rounded-2xl overflow-hidden border border-amber-500/30 shrink-0 shadow-[0_0_30px_rgba(245,158,11,0.3)] group-hover:scale-105 transition-transform duration-500 animate-float-amber">
                {highestRunToken.imageUri ? (
                  <img src={normalizeCoinImageUri(highestRunToken.imageUri, highestRunToken.mint) || undefined} alt={highestRunToken.name || highestRunToken.symbol} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-white/5 text-amber-500/30">
                    <Zap className="size-8" />
                  </div>
                )}
              </div>
              
              {/* Token Info */}
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col min-w-0">
                    <span className="text-[10px] text-amber-500/80 font-black uppercase tracking-widest mb-1 flex items-center gap-1.5">
                      <Flame className="size-3" /> {t("nursery.topRunner")}
                    </span>
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white truncate leading-none drop-shadow-md">
                        {tokenMetaCache.get(highestRunToken.mint)?.name || highestRunToken.name || highestRunToken.symbol || "TOKEN"}
                      </h3>
                      <span className="text-amber-400 font-mono text-sm md:text-base font-bold bg-amber-500/20 px-2 py-0.5 rounded border border-amber-500/30 shrink-0 shadow-[0_0_10px_rgba(245,158,11,0.2)]">
                        ${tokenMetaCache.get(highestRunToken.mint)?.symbol || highestRunToken.symbol}
                      </span>
                    </div>
                  </div>
                  
                  {/* Multiplier Animation */}
                  <div className="flex flex-col items-start shrink-0">
                    <span className="text-[9px] text-amber-500/60 uppercase font-black tracking-widest mb-0.5">{t("nursery.heatMultiplier")}</span>
                    <div className="flex items-end gap-3">
                      <div className="flex items-center gap-1 text-amber-400 font-black text-3xl md:text-4xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] [text-shadow:0_0_30px_rgba(245,158,11,0.6)] leading-none">
                        <AnimatedNumber value={Math.max(Number(highestRunToken.multiplier) || 0, 0)} />X
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {tokenMetaCache.get(highestRunToken.mint)?.description && (
                  <p className="text-xs text-white/60 mt-2 line-clamp-2 max-w-2xl leading-relaxed">
                    {tokenMetaCache.get(highestRunToken.mint)!.description}
                  </p>
                )}
                
                {/* Metrics */}
                <div className="grid grid-cols-2 md:flex md:items-center gap-3 mt-4 pt-4 border-t border-amber-500/20 flex-wrap">
                  <div className="flex flex-col">
                    <span className="text-[9px] text-white/40 uppercase font-bold tracking-wider mb-0.5">{t("nursery.currentMc")}</span>
                    <span className="text-emerald-400 font-mono font-black text-sm md:text-base">{fmtSol(highestRunToken.marketCapSol || (highestRunToken.marketCapUsd / 150) || 0)}</span>
                  </div>
                  <div className="hidden md:block w-px h-8 bg-white/10 mx-2" />
                  <div className="flex flex-col">
                    <span className="text-[9px] text-white/40 uppercase font-bold tracking-wider mb-0.5">{t("nursery.ath")}</span>
                    <span className="text-amber-400 font-mono font-black text-sm md:text-base">{fmtSol(highestRunToken.peakMcSol || (highestRunToken.peakMcUsd / 150) || 0)}</span>
                  </div>

                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "runners" && (
          <div className="mx-4 mb-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-[10px] text-amber-200/70 leading-snug flex items-start gap-2.5 animate-in fade-in">
            <Info className="size-4 text-amber-400 shrink-0" />
            <div className="flex flex-col gap-1">
              <strong className="text-amber-300 font-black tracking-wider uppercase text-[9px]">{t("nursery.heatScoreLabel")}</strong>
              <p>{t("nursery.heatScoreDesc")}</p>
            </div>
          </div>
        )}

        {groupedList.length === 0 ? (
          tab === "my" ? (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center animate-in fade-in zoom-in-95 duration-500">
              <div className="size-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(16,185,129,0.1)]">
                <Rocket className="size-8 text-emerald-400" />
              </div>
              <h3 className="text-lg font-black text-white mb-2">{t("nursery.smartDeploy")}</h3>
              <p className="text-xs text-[var(--color-fg-dim)] max-w-[260px] mb-6 leading-relaxed">
                {t("nursery.smartDeployDesc")}
              </p>
              <button 
                onClick={openDeployModal}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-black font-black uppercase tracking-widest text-xs transition-all hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(16,185,129,0.4)] group"
              >
                {t("nursery.deployNow")}
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-black/60 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-black/80" />
                </span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              {((tab === "new" && (!snap.counts || snap.counts.new.total === 0)) || (tab === "grad" && (!snap.counts || snap.counts.grad.total === 0)) || (tab === "bonded" && (!snap.counts || snap.counts.bonded.total === 0))) ? (
                <>
                  <RefreshCw className="size-6 text-[var(--color-fg-dim)]/20 mb-3 animate-spin-slow" />
                  <p className="text-[11px] text-[var(--color-fg-dim)] uppercase tracking-widest font-bold">{t("nursery.waitingFeed")}</p>
                </>
              ) : (
                <p className="text-[11px] text-[var(--color-fg-dim)] uppercase tracking-widest font-bold">{t("nursery.noTokensMatch")}</p>
              )}
            </div>
          )
        ) : (
          <div className="relative w-full" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const item = virtualItemsList[virtualRow.index];
              return (
                <div
                  key={virtualRow.index}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  {layoutMode === "lily" ? (
                    <LilyGridRow 
                      groups={item as any[]} 
                      caMintInput={caMintInput} 
                      onOpenToken={handleOpenToken} 
                    />
                  ) : (
                    <GroupedTokenRow 
                      group={item} 
                      tab={tab} 
                      caMintInput={caMintInput} 
                      onOpenToken={handleOpenToken} 
                      onConfigureRule={(addr) => {
                        setRuleTargetAddress(addr);
                        setIsRulesOpen(true);
                      }}
                      onFilterDev={(devAddr) => {
                        setNurserySearch(devAddr);
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <DeployerRulesModal 
        isOpen={isRulesOpen} 
        initialAddress={ruleTargetAddress}
        onClose={() => {
          setIsRulesOpen(false);
          setRuleTargetAddress("");
        }} 
      />
    </div>
  );
}

// ── Formatting ────────────────────────────────────────────────────────────────

function fmtSol(n: number | null | undefined) { 
  if (n == null || isNaN(n)) return "0.0 SOL";
  return n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + " SOL"; 
}
function fmtUsd(n: number | null | undefined) { 
  if (n == null) return "$0";
  if (n >= 1_000_000) return "$" + (n / 1_000_000).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "M";
  if (n >= 1_000) return "$" + (n / 1_000).toLocaleString("en-US", { maximumFractionDigits: 0 }) + "K";
  return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}
function fmtAge(ms: number) {
  if (!ms || ms <= 0) return "?";
  const elapsed = Date.now() - ms;
  if (elapsed < 0) return "now";
  const totalSec = Math.floor(elapsed / 1000);
  if (totalSec < 60) return totalSec + "s";
  const totalMin = Math.floor(totalSec / 60);
  if (totalMin < 60) return totalMin + "m";
  const totalHr = Math.floor(totalMin / 60);
  if (totalHr < 24) {
    const m = totalMin % 60;
    return m > 0 ? `${totalHr}h ${m}m` : `${totalHr}h`;
  }
  const totalDays = Math.floor(totalHr / 24);
  if (totalDays < 7) {
    const h = totalHr % 24;
    return h > 0 ? `${totalDays}d ${h}h` : `${totalDays}d`;
  }
  const totalWeeks = Math.floor(totalDays / 7);
  if (totalWeeks < 4) {
    const d = totalDays % 7;
    return d > 0 ? `${totalWeeks}w ${d}d` : `${totalWeeks}w`;
  }
  const totalMonths = Math.floor(totalDays / 30);
  if (totalMonths < 12) return `${totalMonths}mo`;
  const years = Math.floor(totalMonths / 12);
  const mo = totalMonths % 12;
  return mo > 0 ? `${years}y ${mo}mo` : `${years}y`;
}
function scoreBar(s: number) {
  const bars = Math.min(5, Math.ceil(s / 2));
  return "⚡".repeat(bars);
}

const devTone = (p: number | null | undefined): string => p == null ? 'text-white/40' : p < 5 ? 'text-emerald-400' : p < 10 ? 'text-amber-400' : 'text-rose-400';
const t10Tone = (p: number | null | undefined): string => p == null ? 'text-white/40' : p < 40 ? 'text-emerald-400' : p < 65 ? 'text-amber-400' : 'text-rose-400';
const dipTone = (p: number | null | undefined): string => p == null ? 'text-white/40' : p > -20 ? 'text-emerald-400' : p > -40 ? 'text-amber-400' : 'text-rose-400';
const pctStr = (n: number | null | undefined): string => n == null ? '—' : `${n.toLocaleString("en-US", {minimumFractionDigits: Math.abs(n) >= 10 ? 0 : 1, maximumFractionDigits: Math.abs(n) >= 10 ? 0 : 1})}%`;

// ── Toast Notifications ────────────────────────────────────────────────────────
//
// Bundling strategy: new mints that arrive within a 1.5 s debounce window are
// coalesced into a single "N new tokens deployed" toast to prevent spam.
//



